export const SUPPORTED_LOCALES = ["ar-SA", "en"] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export type MessageParameters = Readonly<
  Record<string, string | number | undefined>
>;

export type PluralMessage = Readonly<
  { other: string } & Partial<Record<Intl.LDMLPluralRule, string>>
>;

export type MessageDefinition = string | PluralMessage;

// Add new semantic keys here first. The Arabic catalog below is then required
// by TypeScript to implement the exact same key set.
const ENGLISH_MESSAGES = {
  "collections.title": "Daily registration collections",
  "collections.range": "Period",
  "collections.all": "Since first dated payment",
  "collections.last30": "Last 30 days",
  "collections.last90": "Last 90 days",
  "collections.count": "Paid invoices",
  "collections.amount": "Collected amount",
  "collections.period": "Displayed period · Saudi time",
  "collections.date": "Date",
  "collections.table": "Show daily data table",
  "collections.description":
    "Moyasar registration invoices only, grouped by the stored successful payment timestamp in the conference project (Saudi time). Excludes refunded/cancelled invoices, bank transfers, sponsorships and other revenue.",
  "collections.help":
    "Hover, tap or focus a day to see its count and amount. Zero days are included. Scroll horizontally for the full period.",
  "collections.missing":
    "{count} paid invoices have no reliable payment date and are excluded from the chart. Their amount:",
  "collections.stale":
    "Showing the last successful synchronization; current-day figures may be incomplete.",
  "collections.unavailable":
    "Daily payment dates are not available in the latest conference snapshot yet.",
  "collections.empty": "No paid registration invoices have been recorded yet.",
  "collections.noDates":
    "The chart will appear when reliable successful-payment dates are available. Synchronization dates are never substituted.",
  "app.documentTitle": "Taibah University Conferences Executive Dashboard",
  "app.name": "Taibah University Conferences",
  "locale.arabic": "Arabic",
  "locale.english": "English",
  "locale.switchTo": "Switch language to {language}",
  "common.resultCount": {
    one: "{count} result",
    other: "{count} results",
  },
  "adminUsers.restricted.title": "User administration",
  "adminUsers.restricted.description": "Administrator access is required.",
  "adminUsers.notice.loadingViewers": "Loading viewers…",
  "adminUsers.notice.directoryReady": "Viewer directory ready",
  "adminUsers.notice.creating": "Sending invitation…",
  "adminUsers.notice.restoring": "Restoring viewer access…",
  "adminUsers.notice.disabling": "Disabling viewer…",
  "adminUsers.notice.disabled": "Viewer access disabled.",
  "adminUsers.notice.alreadyActiveAvailable":
    "Already active — viewer access is available.",
  "adminUsers.notice.pendingRestored":
    "Access restored. The setup email was accepted for sending; inbox delivery is not confirmed.",
  "adminUsers.notice.resetting": "Resetting viewer access…",
  "adminUsers.notice.resetSentActive":
    "Existing sessions revoked. The reset email was accepted for sending; inbox delivery is not confirmed.",
  "adminUsers.notice.resetSentPending":
    "Pending setup. The reset email was accepted for sending; inbox delivery is not confirmed.",
  "adminUsers.notice.resending": "Sending invitation…",
  "adminUsers.notice.alreadyComplete":
    "Already active — setup is already complete.",
  "adminUsers.notice.newSetupSent":
    "Invitation accepted for sending. Inbox delivery is not confirmed. The user still needs to complete setup.",
  "adminUsers.notice.loadingAudit": "Loading audit events…",
  "adminUsers.notice.auditLoaded": "Audit events loaded",
  "adminUsers.creation.created":
    "Invitation accepted for sending. The user chooses a password, signs in, and sets up an authenticator. Inbox delivery is not confirmed.",
  "adminUsers.creation.pending":
    "Setup is pending. An invitation was accepted for sending; inbox delivery is not confirmed. Use Resend invitation if needed.",
  "adminUsers.creation.notSent":
    "The account is pending, but no invitation was sent. Use Send invitation beside the user.",
  "adminUsers.creation.active":
    "Already active — this viewer already has dashboard access.",
  "adminUsers.creation.recoveredActive":
    "Already active — existing viewer access was recovered.",
  "adminUsers.creation.recoveredPending":
    "Pending setup recovered. The invitation was accepted for sending; inbox delivery is not confirmed.",
  "adminUsers.error.invalidEmail": "Enter a valid viewer email address.",
  "adminUsers.error.invalidPassword":
    "Use 14–128 characters with uppercase, lowercase, number, and symbol.",
  "adminUsers.error.notFound":
    "This viewer account could not be found. Refresh the directory.",
  "adminUsers.error.protectedIdentity":
    "This protected identity cannot be converted to a viewer.",
  "adminUsers.error.identityConflict":
    "This email belongs to an existing sign-in identity and cannot be converted to a viewer.",
  "adminUsers.error.profileConflict":
    "This viewer account needs administrator review before setup can continue.",
  "adminUsers.error.disabled":
    "This viewer is disabled. Restore access before resending setup.",
  "adminUsers.error.provisioning":
    "Viewer setup is already in progress. Refresh the directory and try again shortly.",
  "adminUsers.error.deliveryFailed":
    "Invitation sending failed. The account remains pending. Use Resend invitation to retry.",
  "adminUsers.error.identityPermission":
    "Viewer administration is not configured correctly. Contact the system administrator.",
  "adminUsers.error.identityUnavailable":
    "The sign-in service is temporarily unavailable. Try again.",
  "adminUsers.error.profileUnavailable":
    "The viewer directory is temporarily unavailable. Try again.",
  "adminUsers.error.auditUnavailable":
    "The account changed, but its audit record could not be confirmed. Refresh the directory before retrying.",
  "adminUsers.error.disableFailed":
    "The viewer could not be disabled. Try again.",
  "adminUsers.error.resetFailed":
    "Viewer access could not be restored. Try again.",
  "adminUsers.error.permissionDenied":
    "Administrator access is required to manage viewers.",
  "adminUsers.error.sessionExpired":
    "Your secure session expired. Sign in again to continue.",
  "adminUsers.error.network": "The service could not be reached. Try again.",
  "adminUsers.error.service":
    "Viewer administration is temporarily unavailable.",
  "adminUsers.error.invalidResponse":
    "Viewer administration returned an invalid response.",
  "adminUsers.error.request": "The request could not be completed. Try again.",
  "adminUsers.error.withRefreshGuidance":
    "{message} Reload this page to refresh the viewer directory before trying again.",
  "adminUsers.heading": "Account setup and sign-in",
  "adminUsers.description":
    "Add users, manage account setup and resend sign-in instructions. Manage roles and conference access above.",
  "adminUsers.create": "Invite user",
  "adminUsers.summary.aria": "Viewer account summary",
  "adminUsers.summary.active": "Active viewers",
  "adminUsers.summary.disabled": "Disabled viewers",
  "adminUsers.summary.pending": "Pending setup",
  "adminUsers.summary.deliveryFailed": "Delivery failed",
  "adminUsers.filter.label": "Filter users",
  "adminUsers.filter.all": "All users",
  "adminUsers.filter.active": "Active users",
  "adminUsers.filter.pending": "Pending setup",
  "adminUsers.filter.deliveryFailed": "Delivery failed",
  "adminUsers.filter.disabled": "Disabled users",
  "adminUsers.table.caption": "Dashboard viewer accounts",
  "adminUsers.table.user": "User",
  "adminUsers.table.role": "Role",
  "adminUsers.table.status": "Status",
  "adminUsers.table.lastSignIn": "Last sign-in",
  "adminUsers.table.actions": "Actions",
  "adminUsers.role.viewer": "Viewer",
  "adminUsers.status.active": "Active",
  "adminUsers.status.pending": "Pending setup",
  "adminUsers.status.notSent": "Invitation not sent",
  "adminUsers.status.deliveryFailed": "Delivery failed",
  "adminUsers.status.disabled": "Disabled",
  "adminUsers.notSignedIn": "Not yet signed in",
  "adminUsers.action.working": "Working…",
  "adminUsers.action.restore": "Restore access",
  "adminUsers.action.disable": "Disable",
  "adminUsers.action.resetting": "Resetting…",
  "adminUsers.action.reset": "Reset access",
  "adminUsers.action.resending": "Sending…",
  "adminUsers.action.send": "Send invitation",
  "adminUsers.action.resend": "Resend invitation",
  "adminUsers.empty": "No viewers match this filter.",
  "adminUsers.audit.heading": "Administrative audit events",
  "adminUsers.audit.refresh": "Refresh audit events",
  "adminUsers.audit.action.viewerCreated": "Viewer created",
  "adminUsers.audit.action.userDeletionStarted": "User deletion started",
  "adminUsers.audit.action.userDeleted": "User permanently deleted",
  "adminUsers.audit.action.userDeletionDenied": "User deletion denied",
  "adminUsers.audit.action.userDeletionFailed": "User deletion incomplete",
  "adminUsers.audit.action.viewerReconciled": "Viewer reconciled",
  "adminUsers.audit.action.mfaActivated": "Viewer MFA activated",
  "adminUsers.audit.action.setupResent": "Viewer setup resent",
  "adminUsers.audit.action.viewerDisabled": "Viewer disabled",
  "adminUsers.audit.action.accessReset": "Viewer access reset",
  "adminUsers.audit.action.managementDenied": "Viewer management denied",
  "adminUsers.audit.action.signIn": "Signed in",
  "adminUsers.audit.action.signOut": "Signed out",
  "adminUsers.audit.action.mfaEnrolled": "Multi-factor authentication enrolled",
  "adminUsers.audit.action.signInDenied": "Sign-in denied",
  "adminUsers.audit.action.conferenceDisabled": "Conference disabled",
  "adminUsers.audit.action.conferenceDiscovered": "Conference discovered",
  "adminUsers.audit.action.conferenceRegistrationRequested":
    "Conference registration requested",
  "adminUsers.audit.action.conferenceRegistrationActivated":
    "Conference registration activated",
  "adminUsers.audit.action.conferenceManagementDenied":
    "Conference management denied",
  "adminUsers.audit.action.brandingUploaded": "Conference branding uploaded",
  "adminUsers.audit.action.brandingRemoved": "Conference branding removed",
  "adminUsers.audit.action.brandingManagementDenied":
    "Conference branding management denied",
  "adminUsers.audit.action.sponsorshipCreated": "Sponsorship created",
  "adminUsers.audit.action.sponsorshipUpdated": "Sponsorship updated",
  "adminUsers.audit.action.sponsorshipInvoiceRecorded":
    "Sponsorship invoice recorded",
  "adminUsers.audit.action.sponsorshipPaymentRecorded":
    "Sponsorship payment recorded",
  "adminUsers.audit.action.sponsorshipManagementDenied":
    "Sponsorship management denied",
  "adminUsers.audit.action.otherRevenueCreated": "Other revenue created",
  "adminUsers.audit.action.otherRevenueUpdated": "Other revenue updated",
  "adminUsers.audit.action.otherRevenueReversed": "Other revenue reversed",
  "adminUsers.audit.action.otherRevenueManagementDenied":
    "Other revenue management denied",
  "adminUsers.audit.action.ledgerEntryCreated": "Ledger entry created",
  "adminUsers.audit.action.ledgerEntryReversed": "Ledger entry reversed",
  "adminUsers.audit.action.other": "Administrative activity",
  "adminUsers.audit.outcome.success": "Succeeded",
  "adminUsers.audit.outcome.denied": "Denied",
  "adminUsers.audit.outcome.failed": "Failed",
  "adminUsers.drawer.description":
    "Enter the email address only. The invitation lets the user choose a password, sign in, then set up Google Authenticator before accessing the dashboard.",
  "adminUsers.drawer.close": "Close user invitation",
  "adminUsers.drawer.email": "User email",
  "adminUsers.drawer.deliveryHint":
    "No password is shared with the administrator or sent by email. The invitation is always in Arabic. Sending status does not confirm inbox delivery.",
  "adminUsers.drawer.access": "Access",
  "adminUsers.drawer.readOnly":
    "No conferences until an administrator grants access",
  "adminUsers.drawer.cancel": "Cancel",
  "adminUsers.drawer.sending": "Sending…",
  "adminUsers.drawer.send": "Send invitation",
  "finance.method.bankTransfer": "Bank transfer",
  "finance.method.bankDeposit": "Bank deposit",
  "finance.method.other": "Other",
  "finance.field.reference": "Transfer or receipt reference",
  "finance.field.evidence": "Evidence document reference",
  "finance.field.referenceHint":
    "Enter the transfer or receipt reference, such as PAY-2026-001. Do not enter personal details.",
  "finance.field.evidenceHint":
    "Enter the document identifier from your records, such as DOC-001. This field saves a reference only; it does not upload a file.",
  "finance.field.referenceInvalid":
    "Use 2–160 English letters or numbers, with . _ : or - if needed. Start with a letter or number; do not use spaces or personal details.",
  "finance.field.localTimeHint":
    "Date and time use your device’s local time zone.",
  "finance.form.requiredHint": "All fields are required.",
  "finance.form.contextLocked":
    "Save or cancel your draft before choosing a different conference.",
  "finance.form.leaveConfirm":
    "You have unsaved changes. Leave this page and discard them?",
  "finance.form.referencesHeading": "References and evidence",
  "finance.form.cancel": "Cancel entry",
  "finance.field.method": "Method",
  "finance.field.status": "Status",
  "finance.placeholder.amount": "0.00",
  "finance.action.tryAgain": "Try again",
  "finance.action.saving": "Saving…",
  "otherRevenue.category.exhibition": "Exhibition",
  "otherRevenue.category.workshop": "Workshop",
  "otherRevenue.category.grant": "Grant",
  "otherRevenue.category.donation": "Donation",
  "otherRevenue.category.service": "Service",
  "otherRevenue.category.other": "Other",
  "otherRevenue.status.active": "Active",
  "otherRevenue.status.reversed": "Reversed",
  "otherRevenue.form.invalid":
    "Enter a valid positive amount and collection date.",
  "otherRevenue.form.recorded": "Other revenue entry recorded.",
  "otherRevenue.form.failed": "The revenue entry could not be recorded.",
  "otherRevenue.form.add": "Add other revenue",
  "otherRevenue.form.source": "Paying organization",
  "otherRevenue.form.category": "Category",
  "otherRevenue.form.description": "Revenue description",
  "otherRevenue.form.detailsHeading": "Revenue details",
  "otherRevenue.form.amount": "Collected amount ({currency})",
  "otherRevenue.form.amountLabel": "Collected amount",
  "otherRevenue.optional": "optional",
  "otherRevenue.actions": "Actions",
  "otherRevenue.conferenceHeading": "Conference revenue",
  "otherRevenue.form.requiredHint":
    "Collected amount and revenue description are required. Transfer/receipt and evidence references are optional.",
  "otherRevenue.edit.action": "Edit",
  "otherRevenue.edit.heading": "Edit revenue",
  "otherRevenue.edit.save": "Save changes",
  "otherRevenue.edit.saved": "Revenue updated and totals recalculated.",
  "otherRevenue.edit.failed":
    "Could not save this change. The record may have changed or been reversed. Cancel, refresh the page, and try again.",
  "otherRevenue.form.collectionMethod": "Collection method",
  "otherRevenue.form.collectedAt": "Collected at",
  "otherRevenue.form.record": "Add revenue",
  "otherRevenue.heading": "Other revenue",
  "otherRevenue.description":
    "Collected income from other sources, such as exhibitions, workshops, grants and donations. Registration fees and sponsorships are recorded separately.",
  "otherRevenue.loading": "Loading other revenue…",
  "otherRevenue.loadFailed": "Other revenue could not be loaded.",
  "otherRevenue.empty": "No other revenue has been recorded.",
  "otherRevenue.reverse.heading": "Reverse entry",
  "otherRevenue.reverse.description":
    "The amount will be excluded from totals. The original record and a matching reversal will be retained.",
  "otherRevenue.reverse.busy": "Reversing…",
  "otherRevenue.reverse.confirm": "Confirm reversal",
  "otherRevenue.reverse.failed": "The revenue entry could not be reversed.",
  "otherRevenue.reverse.recorded": "Reversed by an append-only ledger entry.",
  "sponsorship.form.invalidMovement": "Enter a valid positive amount and date.",
  "sponsorship.form.invoiceRecorded": "Invoice recorded.",
  "sponsorship.form.paymentRecorded": "Payment recorded.",
  "sponsorship.form.invoiceFailed":
    "The invoice could not be recorded. Check the sponsorship value not yet invoiced.",
  "sponsorship.form.paymentFailed":
    "The payment could not be recorded. Check the unpaid invoice balance.",
  "sponsorship.form.recordInvoice": "Record invoice",
  "sponsorship.form.recordPayment": "Record payment",
  "sponsorship.form.amount": "Amount ({currency})",
  "sponsorship.form.amountLabel": "Amount",
  "sponsorship.form.valueLabel": "Sponsorship value",
  "sponsorship.form.paymentMethod": "Payment method",
  "sponsorship.form.invoiceReference": "Invoice reference",
  "sponsorship.form.invoiceReferenceHint":
    "Enter the invoice reference, such as INV-2026-001. Do not enter personal details.",
  "sponsorship.form.paymentReference": "Transfer or receipt reference",
  "sponsorship.form.referenceHint":
    "Enter your sponsorship reference, such as SP-2026-001. Do not enter personal details.",
  "sponsorship.form.detailsHeading": "Sponsorship details",
  "sponsorship.form.issuedAt": "Issued at",
  "sponsorship.form.receivedAt": "Received at",
  "sponsorship.form.saveInvoice": "Save invoice",
  "sponsorship.form.savePayment": "Save payment",
  "sponsorship.form.invalidContract":
    "Enter a sponsorship value greater than zero.",
  "sponsorship.form.created": "Sponsorship added.",
  "sponsorship.form.createFailed":
    "The sponsorship could not be added. Check the fields and try again.",
  "sponsorship.form.add": "Add sponsorship",
  "sponsorship.form.organization": "Sponsor organization",
  "sponsorship.form.package": "Package",
  "sponsorship.form.packagePlaceholder": "Platinum, Gold, custom…",
  "sponsorship.form.contractValue": "Sponsorship value ({currency})",
  "sponsorship.form.agreementReference": "Sponsorship reference",
  "sponsorship.form.creating": "Adding…",
  "sponsorship.form.create": "Add sponsorship",
  "sponsorship.unavailable.heading": "Sponsorships are unavailable",
  "sponsorship.unavailable.description":
    "Sponsorship data could not be loaded. Try again.",
  "sponsorship.management.aria": "Sponsorship management",
  "sponsorship.conference": "Conference",
  "sponsorship.totals.aria": "Sponsorship totals",
  "sponsorship.metric.contracted": "Sponsorship value",
  "sponsorship.metric.invoiced": "Invoice value issued",
  "sponsorship.metric.collected": "Amount collected",
  "sponsorship.metric.outstanding": "Remaining sponsorship value",
  "sponsorship.loading": "Loading sponsorships…",
  "sponsorship.empty.heading": "No sponsorships recorded",
  "sponsorship.empty.description":
    "Add a sponsorship to track its value, invoices and payments.",
  "sponsorship.evidence": "Evidence document:",
  "connect.capability.automated": "Automated",
  "connect.capability.unavailable": "Unavailable",
  "connect.manifestDiscovered": "Connector manifest discovered",
  "connect.metadataLocked":
    "Conference metadata is sourced from the private connector manifest and cannot be edited here.",
  "connect.field.projectId": "Project ID",
  "connect.field.conferenceKey": "Conference key",
  "connect.field.starts": "Starts",
  "connect.field.ends": "Ends",
  "connect.field.timezone": "Timezone",
  "connect.field.connectorRegion": "Connector region",
  "connect.field.connectorVersion": "Connector version",
  "connect.field.adapterVersion": "Adapter version",
  "connect.field.sourceVersion": "Source version",
  "connect.objective.title": "Conference objective",
  "connect.objective.empty": "No objective supplied by the connector.",
  "connect.coverage.title": "Automated data coverage",
  "connect.coverage.registrations": "Registrations",
  "connect.coverage.payments": "Payments",
  "connect.coverage.analytics": "Google Analytics",
  "connect.activation.title": "Administrator activation",
  "connect.activation.body":
    "The project pipeline must first register this same verified identity as pending. Activation is a separate audited action.",
  "connect.activation.complete": "Conference activated",
  "connect.activation.activating": "Activating…",
  "connect.activation.action": "Activate pending conference",
  "connect.activation.error":
    "Activation was not accepted. Confirm that the verified pipeline registration is pending.",
  "connect.discovery.kicker": "Connect conference data",
  "connect.discovery.title": "Enter the Firebase project ID",
  "connect.discovery.body":
    "The connector supplies the conference name, logo, objective and dates automatically.",
  "connect.discovery.projectPlaceholder": "conference-2027",
  "connect.discovery.manualOnly":
    "Enter the project ID, not the conference name or website address.",
  "connect.discovery.discovering": "Retrieving conference data…",
  "connect.discovery.action": "Get conference data",
  "connect.discovery.sessionExpired":
    "Your session expired. Sign in again to continue.",
  "connect.discovery.notFound":
    "A trusted connector could not be discovered for this project.",
  "connect.preview.title": "Conference preview",
  "connect.preview.empty":
    "Enter a project ID and choose Get conference data to see a preview.",
  "connect.status.planned": "Planned",
  "connect.status.active": "Active",
  "connect.status.ended": "Ended",
  "connect.status.archived": "Archived",
  "branding.asset.icon": "Square icon",
  "branding.asset.logo": "Full logo",
  "branding.asset.iconDescription":
    "Used in conference lists and compact navigation. Use a near-square image.",
  "branding.asset.logoDescription":
    "Used in the conference header and detailed views. Any aspect ratio is supported.",
  "branding.source.connector": "Connector source · No admin override",
  "branding.source.admin":
    "Admin upload · Cache version {version} · {width} × {height} px",
  "branding.previewAlt": "{conferenceName} {asset} preview",
  "branding.checkingDimensions": "Checking image dimensions…",
  "branding.error.dimensionsUnreadable":
    "The image dimensions could not be read. Choose a different file.",
  "branding.error.previewUnavailable":
    "The image preview could not be prepared. Choose a different file.",
  "branding.updated": "{asset} updated. The new cache version is active.",
  "branding.removed":
    "{asset} override removed. The connector source is active.",
  "branding.title": "Conference branding",
  "conferenceEditor.open": "Edit conference details",
  "conferenceEditor.title": "Conference names",
  "conferenceEditor.description":
    "Set the name shown in each language. These names stay in place when conference data is synchronized. Manage the images below.",
  "conferenceEditor.ar": "Arabic name",
  "conferenceEditor.en": "English name",
  "conferenceEditor.preview": "Name preview",
  "conferenceEditor.save": "Save names",
  "conferenceEditor.saving": "Saving…",
  "conferenceEditor.cancel": "Discard changes",
  "conferenceEditor.saved": "Conference names saved.",
  "conferenceEditor.error":
    "Names could not be saved. Your entries are preserved; please try again.",
  "conferenceEditor.invalid":
    "Enter both names, up to 160 characters each, without markup or control characters.",
  "conferenceEditor.unsaved": "You have unsaved changes.",
  "conferenceWebsite.title": "Official conference website",
  "conferenceWebsite.label": "Website URL",
  "conferenceWebsite.description":
    "Enter the full HTTPS URL, including your conference's custom domain. Save the website separately from the names and images. Leave it blank and save to restore the default website.",
  "conferenceWebsite.default": "Default website:",
  "conferenceWebsite.save": "Save website",
  "conferenceWebsite.saved": "Official conference website saved.",
  "conferenceWebsite.reset": "The default conference website is restored.",
  "conferenceWebsite.error":
    "The website could not be saved. Your entry is preserved; please try again.",
  "conferenceWebsite.invalid":
    "Enter a full HTTPS URL with a public domain, without login credentials or control characters. Leave it blank to restore the default website.",
  "branding.description":
    "Preview the compact icon and full logo used across the executive dashboard.",
  "branding.access.admin": "Admin controls",
  "branding.access.readOnly": "Read-only",
  "branding.readyToUpload": "Ready to upload · {size} MiB",
  "branding.chooseImage": "Choose image",
  "branding.uploading": "Uploading…",
  "branding.upload": "Upload",
  "branding.removing": "Removing…",
  "branding.removeOverride": "Remove override",
  "branding.constraints":
    "PNG, JPEG, WebP or safe SVG · {maxMiB} MiB maximum · {minPixels}–{maxPixels} px on each side · {maxMegapixels} megapixels maximum. Icons must use a near-square {minAspect}–{maxAspect} aspect ratio. Removing an override restores the connector source.",
  "branding.viewerNote":
    "Viewers can inspect branding versions but cannot upload or remove assets.",
  "branding.error.fileTooLarge": "Choose an image no larger than {maxMiB} MiB.",
  "branding.error.unsupportedType": "Choose a PNG, JPEG, WebP or SVG image.",
  "branding.error.iconAspect":
    "Choose a near-square icon with an aspect ratio between {minAspect} and {maxAspect}.",
  "branding.error.invalidDimensions":
    "Choose an image between {minPixels} and {maxPixels} px on each side and no larger than {maxMegapixels} megapixels.",
  "branding.error.emptyFile":
    "The selected image is empty. Choose a different file.",
  "branding.error.uploadLimit":
    "The image exceeds the {maxMiB} MiB upload limit.",
  "branding.error.invalidImage":
    "The image was rejected. Check its format, dimensions and safe SVG content, then try again.",
  "branding.error.conferenceNotFound":
    "This conference is no longer available. Refresh the page before trying again.",
  "branding.error.storageUnavailable":
    "Branding storage is temporarily unavailable. Try again later.",
  "branding.error.sessionExpired":
    "Your session expired. Sign in again before changing conference branding.",
  "branding.error.saveFailed":
    "The branding change could not be saved. Try again.",
  "map.title": "Visitors by country · last 30 days",
  "map.visitorAria": {
    one: "{country}: {count} visitor in the last 30 days",
    other: "{country}: {count} visitors in the last 30 days",
  },
  "map.description":
    "World choropleth showing visitors for countries with available map boundaries. Complete country and unmatched analytics data follows the map.",
  "map.visitorCount": {
    one: "{count} visitor",
    other: "{count} visitors",
  },
  "map.legend.lower": "Lower",
  "map.legend.higher": "Higher",
  "map.allCountryData": {
    one: "All country data ({count} visitor)",
    other: "All country data ({count} visitors)",
  },
  "map.boundaryUnavailable": "Boundary unavailable",
  "map.noRecognizedCountry":
    "No recognized country aggregate is available for this period.",
  "map.unmappedTitle": "Unmapped analytics data",
  "map.emptyCountryValue": "Empty country value",
  "map.none": "None.",
  "detail.loading": "Loading conference details",
  "detail.error.title": "Conference details unavailable",
  "detail.error.description":
    "The aggregate conference record could not be loaded.",
  "detail.breadcrumb.conferences": "Conferences",
  "detail.website.open": "Open conference website",
  "detail.website.ariaLabel": "Open the website for {conference}",
  "detail.lastSynchronized": "Last synchronized {date}",
  "detail.notice.upgradeTitle": "Connector upgrade required",
  "detail.notice.upgradeDescription":
    "The latest safe aggregates remain visible while an administrator updates this conference connector.",
  "detail.notice.delayedTitle": "Data refresh delayed",
  "detail.notice.delayedDescription":
    "Figures show the last successful aggregate synchronization.",
  "detail.tabs.ariaLabel": "Conference details",
  "detail.tab.overview": "Overview",
  "detail.tab.analytics": "Analytics",
  "detail.tab.finance": "Finance",
  "detail.tab.information": "Information",
  "detail.trend.title": "Registration trend",
  "detail.trend.empty": "Trend appears after two successful daily snapshots.",
  "detail.trend.last30Days": "Registrations · last 30 days",
  "detail.trend.ariaLabel": "Registration total trend over the last 30 days",
  "detail.overview.title": "Conference overview",
  "detail.overview.totalRegistrations": "Total registrations",
  "detail.overview.completed": "Completed",
  "detail.overview.pending": "Pending",
  "detail.overview.awaitingPayment": "Awaiting payment",
  "detail.overview.incomplete": "Incomplete registrations",
  "detail.overview.otherIncomplete": "Other incomplete registrations",
  "detail.overview.otherIncompleteHint":
    "Other incomplete registrations are not awaiting an active invoice payment, such as expired invoices or registrations without an issued invoice.",
  "detail.overview.cancelled": "Cancelled",
  "detail.overview.financialSummary": "Financial summary",
  "detail.overview.registrationRevenueCollected":
    "Registration revenue collected",
  "detail.overview.invoicesIssued": "Invoices issued",
  "detail.overview.outstandingInvoices": "Outstanding invoices",
  "detail.overview.sponsorshipRevenue": "Sponsorship revenue",
  "detail.overview.otherRevenue": "Other revenue",
  "detail.purpose.title": "Purpose",
  "detail.purpose.empty":
    "No purpose has been provided by the conference source.",
  "detail.dataStatus.title": "Data status",
  "detail.dataStatus.registration": "Registration data",
  "detail.dataStatus.payment": "Payment data",
  "detail.dataStatus.analytics": "Analytics data",
  "detail.dataStatus.available": "Available",
  "detail.dataStatus.stale": "Delayed",
  "detail.dataStatus.unavailable": "Unavailable",
  "detail.invoiceStatus.notIssued": "Not issued",
  "detail.invoiceStatus.outstanding": "Outstanding",
  "detail.invoiceStatus.partiallyCollected": "Partially collected",
  "detail.invoiceStatus.collected": "Collected",
  "detail.finance.integrityKicker": "Data integrity",
  "detail.finance.unavailableTitle": "Finance unavailable",
  "detail.finance.unavailableDescription":
    "The financial position could not be reconciled at the smallest currency unit. No partial or estimated values are shown.",
  "detail.finance.executiveKicker": "Executive view",
  "detail.finance.positionTitle": "Collection summary",
  "detail.finance.currencyAria": "Currency {currency}",
  "detail.finance.collectedBasis": "collected basis",
  "detail.finance.totalCollected": "Total collected",
  "detail.finance.pendingCollection": "Pending collection",
  "detail.finance.totalExpenses": "Total expenses",
  "detail.finance.availableBalance": "Available balance",
  "detail.finance.deficit": "Budget deficit",
  "detail.finance.expenseDetails": "Expense details",
  "detail.finance.cashReceived": "Amounts received",
  "detail.finance.cashReceivedDescription":
    "The total actually received from registration fees, sponsorship payments and other revenue. Amounts that have not been paid are excluded.",
  "detail.finance.componentsKicker": "By source",
  "detail.finance.revenueMixTitle": "Collected revenue by source",
  "detail.finance.revenueMixDescription":
    "These figures show amounts actually received from each source. Unpaid invoices and uncollected sponsorship commitments are not included.",
  "detail.finance.registrationCollected": "Registration fees collected",
  "detail.finance.sponsorshipCollected": "Sponsorship payments collected",
  "detail.finance.otherRevenueCollected": "Other revenue collected",
  "detail.finance.registrationOnlyKicker": "Registration only",
  "detail.finance.invoiceStatusTitle": "Invoice status",
  "detail.finance.collectionRate": "Collection rate by invoice value",
  "detail.finance.noInvoicesIssued":
    "No registration invoices have been issued.",
  "detail.finance.collectionRateAria": "Registration invoice collection rate",
  "detail.finance.invoiceValueIssued": "Registration invoice value issued",
  "detail.finance.outstandingBalance":
    "Outstanding registration invoice balance",
  "detail.finance.invoicesIssued": "Invoices issued",
  "detail.finance.invoicesCollected": "Invoices collected",
  "detail.finance.invoicesOutstanding": "Invoices outstanding",
  "detail.finance.invoiceNote":
    "These figures cover registration invoices only, not sponsorship invoices. The outstanding balance is the invoice value issued minus the amount collected.",
  "detail.information.title": "Conference information",
  "detail.information.conference": "Conference",
  "detail.information.firebaseProject": "Firebase project",
  "detail.information.starts": "Starts",
  "detail.information.ends": "Ends",
  "detail.information.connection": "Connection",
  "detail.information.lastSynchronized": "Last synchronized",
  "detail.analytics.dailyVisitors": "Daily visitors",
  "detail.analytics.dailyEmpty": "Daily GA4 trend is not available yet.",
  "detail.analytics.dailyAria": "Daily active user trend over the last 30 days",
  "detail.analytics.unavailableTitle": "Analytics temporarily unavailable",
  "detail.analytics.unavailableDescription":
    "Registration and financial aggregates remain available while the GA4 source reconnects.",
  "detail.analytics.audienceTitle": "Active users by period",
  "detail.analytics.today": "Today",
  "detail.analytics.yesterday": "Yesterday",
  "detail.analytics.thisMonth": "This month",
  "detail.analytics.totalUsers": "Total website visitors",
  "detail.analytics.trackingPeriod": "Available analytics: {start}–{end}",
  "detail.analytics.trackingSince": "First available analytics: {start}",
  "detail.analytics.lifetimeUnavailable":
    "The total for the available analytics period is unavailable",
  "detail.analytics.lifetimeNote":
    "GA4 total users since the first available analytics date, not a sum of daily counts. One user can visit on multiple days.",
  "detail.analytics.last30Minutes": "Last 30 minutes",
  "detail.analytics.estimateNote":
    "Google Analytics estimates may be delayed. These period cards count active users, not page views; the overall total above includes all users who recorded an event.",
  "detail.analytics.topCountries": "Top countries",
  "detail.analytics.noCountries":
    "No country aggregate is available for this period.",
  "common.unavailable": "Unavailable",
  "common.notApplicable": "Not applicable",
  "common.notSynchronized": "Not synchronized",
  "common.tryAgain": "Try again",
  "common.cancel": "Cancel",
  "session.expired.title": "Your session expired",
  "session.expired.description":
    "Sign out and sign in again to continue securely.",
  "access.denied.title": "Dashboard access restricted",
  "access.error.accountInactive":
    "Your dashboard access is not active. Contact the system administrator to complete activation or account recovery.",
  "access.error.mfaEnrollmentRequired":
    "Multi-factor authentication setup is required. Sign out and sign in again to complete setup. Contact the system administrator if access remains blocked.",
  "access.error.mfaChallengeRequired":
    "Multi-factor verification is required. Sign out and sign in again, then enter your authenticator code.",
  "access.error.providerNotAllowed":
    "This sign-in method is not allowed for your account. Sign out and use your assigned sign-in method.",
  "access.error.profileNotFound":
    "Dashboard access has not been configured for your account. Contact the system administrator.",
  "access.error.unverifiedEmail":
    "Verify your account email, then sign out and sign in again. Contact the system administrator if you need help.",
  "access.error.permissionDenied":
    "You do not have permission to access these data or perform this action. Contact the system administrator if you need access.",
  "sync.notSynchronized": "Not synchronized",
  "sync.noSuccessfulSynchronization": "No successful synchronization",
  "sync.recently": "Recently synchronized",
  "sync.synchronizedRelative": "Synchronized {relative}",
  "sync.dataMayBeDelayed": "Data may be delayed",
  "sync.sourcesFresh": "Sources fresh",
  "sync.sourceDelay": "Source delay",
  "conference.lifecycle.active": "Active",
  "conference.lifecycle.upcoming": "Upcoming",
  "conference.lifecycle.ended": "Ended",
  "conference.connection.connected": "Connected",
  "conference.connection.degraded": "Delayed",
  "conference.connection.neverSynchronized": "Not synchronized",
  "conference.connection.upgradeRequired": "Upgrade required",
  "conference.connection.disabled": "Disabled",
  "metric.totalConferences": "Total conferences",
  "metric.totalConferences.definition":
    "All conferences available to this account.",
  "metric.activeConferences": "Active conferences",
  "metric.activeConferences.definition":
    "Conferences whose event dates include today.",
  "metric.totalRegistrations": "Total registrations",
  "metric.totalRegistrations.definition":
    "All registration states in the latest source snapshots.",
  "metric.netCollected": "Net collected",
  "metric.netCollected.definition":
    "Collected registration fees plus received sponsorships and other revenue.",
  "metric.outstandingInvoices": "Outstanding invoices",
  "metric.outstandingInvoices.definition":
    "Issued registration invoices that remain unpaid.",
  "metric.sponsorshipRevenue": "Sponsorship revenue",
  "metric.sponsorshipRevenue.definition":
    "Sponsorship payments received and recorded centrally.",
  "metric.otherRevenue": "Other revenue",
  "metric.otherRevenue.definition":
    "Other received revenue recorded centrally.",
  "metric.visitorsThisMonth": "Visitors this month",
  "metric.visitorsThisMonth.definition":
    "Estimated GA4 active users for the current month.",
  "table.conference": "Conference",
  "table.dates": "Dates",
  "table.status": "Status",
  "table.registrations": "Registrations",
  "table.collected": "Collected",
  "table.visitors": "Visitors",
  "table.lastSynchronized": "Last synchronized",
  "table.connection": "Connection",
  "table.action": "Action",
  "overview.loading": "Loading portfolio overview",
  "overview.metrics.ariaLabel": "Portfolio metrics",
  "overview.error.title": "Overview unavailable",
  "overview.error.description":
    "The latest aggregate data could not be loaded. No stored value was replaced with zero.",
  "overview.empty.title": "No conferences are available",
  "overview.empty.adminDescription":
    "Connect a Firebase conference project to begin the portfolio.",
  "overview.empty.viewerDescription":
    "Your account has not been granted access to a conference yet.",
  "overview.sourceDelayWarning":
    "Some sources are delayed. Last successful values remain visible.",
  "overview.performance.title": "Conference performance",
  "overview.performance.description":
    "Latest aggregate snapshot for each connected conference.",
  "overview.performance.caption":
    "Conference registration, revenue, visitor, and synchronization aggregates",
  "overview.performance.viewConference": "View conference",
  "overview.visitors.title": "Portfolio visitors",
  "overview.visitors.unavailable": "Not available for these conferences",
  "overview.visitors.trendAriaLabel": "Portfolio visitor trend",
  "overview.visitors.historyAccumulating":
    "Trend history is still accumulating.",
  "overview.analytics.estimateNotice":
    "Analytics values are estimates and may be delayed.",
  "overview.analytics.partialCoverage": "Partial analytics coverage",
  "overview.analytics.last30Days": "Last 30 days of available snapshots",
  "directory.loading": "Loading conferences",
  "directory.error.title": "Conference list unavailable",
  "directory.error.description":
    "The aggregate conference list could not be loaded.",
  "directory.controls.ariaLabel": "Conference list controls",
  "directory.filters.ariaLabel": "Filter conferences",
  "directory.filter.all": "All",
  "directory.search.label": "Search conferences",
  "directory.search.placeholder": "Search conferences…",
  "directory.sort.label": "Sort by",
  "directory.sort.eventDate": "Event date",
  "directory.sort.name": "Name",
  "directory.sort.lastSynchronized": "Last synchronized",
  "directory.empty.title": "No matching conferences",
  "directory.empty.description": "Change the status filter or search term.",
  "directory.table.caption":
    "Connected conference dates, metrics, synchronization, and connection status",
  "directory.action.open": "Open",
  "directory.action.disable": "Disable",
  "directory.disable.title": "Disable conference connection?",
  "directory.disable.descriptionSuffix":
    "will remain visible with its last successful aggregate snapshot. New synchronization will not reactivate it.",
  "directory.disable.error": "The conference could not be disabled.",
  "directory.disable.busy": "Disabling…",
  "directory.disable.confirm": "Disable conference",
  "brand.taibahUniversity": "Taibah University",
  "brand.taibahValley": "Taibah Valley",
  "brand.deliveredBy": "Delivered by",
  "brand.partnerSignature":
    "Designed, developed and delivered by Taibah Valley",
  "brand.partnerCredit": "Designed, developed & delivered by",
  "brand.universityIdentityAria": "Taibah University identity",
  "auth.dashboardLabel": "Taibah University Conferences Executive Dashboard",
  "auth.subtitle": "Sign in to monitor conference performance.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "auth.signingIn": "Signing in…",
  "auth.signIn": "Sign in",
  "auth.or": "or",
  "auth.embeddedTitle": "Google sign-in requires Chrome or Safari.",
  "auth.embeddedBody":
    "This embedded browser is not supported by Google. Open this same dashboard link in a standalone browser to continue.",
  "auth.openDashboardLink": "Open dashboard link",
  "auth.continueGoogle": "Continue with Google",
  "auth.oauthHelp":
    "If this dashboard is open inside another app, copy its link and open it in standalone Chrome or Safari before using Google sign-in.",
  "auth.authorizedOnly": "Authorized users only.",
  "auth.signOut": "Sign out",
  "auth.mfaConfiguredNotice":
    "MFA is configured. Sign in again to verify your code.",
  "auth.error.invalidCredentials": "The email or password is incorrect.",
  "auth.error.accountDisabled": "This account has been disabled.",
  "auth.error.accountNotProvisioned":
    "This Google account has not been provisioned for the dashboard.",
  "auth.error.accessDenied":
    "This account is not authorized for the dashboard.",
  "auth.error.googleUnavailable":
    "Google sign-in could not open. Allow pop-ups or use standalone Chrome or Safari, then try again.",
  "auth.error.invalidMfa": "The verification code is invalid or has expired.",
  "auth.error.network": "The service could not be reached. Try again.",
  "auth.error.popupClosed": "Google sign-in was cancelled.",
  "auth.error.sessionExpired": "Your secure session expired. Sign in again.",
  "auth.error.unexpected": "Sign-in could not be completed.",
  "mfa.enrollTitle": "Set up multi-factor authentication",
  "mfa.challengeTitle": "Verify your identity",
  "mfa.enrollInstruction":
    "Add this account to your authenticator app, then enter the six-digit code.",
  "mfa.challengeInstruction":
    "Enter the six-digit code from your authenticator app.",
  "mfa.scanTitle": "Scan the QR code",
  "mfa.scanHelp": "Use Google Authenticator or Microsoft Authenticator.",
  "mfa.qrAlt": "Authenticator setup QR code for {issuer}, account {account}",
  "mfa.qrUnavailable": "QR code unavailable",
  "mfa.qrUnavailableBody": "Use the setup key, or try creating the QR again.",
  "mfa.qrRetry": "Try QR again",
  "mfa.qrLoading": "Creating QR code…",
  "mfa.accountTitle": "Confirm the account",
  "mfa.accountHelp": "These details will appear in your authenticator app.",
  "mfa.service": "Service",
  "mfa.account": "Account",
  "mfa.manualTitle": "Can’t scan the QR code?",
  "mfa.manualHelp": "Enter this setup key in your authenticator app instead.",
  "mfa.manualKeyAria": "Manual setup key",
  "mfa.copying": "Copying…",
  "mfa.copyKey": "Copy setup key",
  "mfa.keyCopied": "Setup key copied.",
  "mfa.copyFailed": "Copy failed. Select and copy the key manually.",
  "mfa.verifySetupTitle": "Verify the setup",
  "mfa.verifySetupHelp":
    "Enter the current six-digit code to finish enrollment.",
  "mfa.codeLabel": "Verification code",
  "mfa.verifying": "Verifying…",
  "mfa.finishSetup": "Finish setup",
  "mfa.verifyCode": "Verify code",
  "mfa.backToSignIn": "Back to sign in",
  "nav.overview": "Overview",
  "nav.conferences": "Conferences",
  "nav.revenue": "Revenue",
  "nav.expenses": "Expenses",
  "nav.users": "Users",
  "nav.connect": "Connect conference",
  "nav.more": "More",
  "a11y.skipMain": "Skip to main content",
  "a11y.primaryNavigation": "Primary navigation",
  "a11y.mobileNavigation": "Mobile navigation",
  "a11y.moreNavigation": "More navigation",
  "a11y.restoringSession": "Restoring secure session",
  "role.executiveAdmin": "System administrator",
  "role.executiveViewer": "Read-only access",
  "role.executiveAdminInitials": "SA",
  "role.executiveViewerInitials": "RO",
  "theme.useLight": "Use light theme",
  "theme.useDark": "Use dark theme",
  "session.authenticated": "Signed in",
  "page.overviewTitle": "Conference portfolio",
  "page.overviewSubtitle": "Executive overview",
  "page.conferencesTitle": "Conferences",
  "page.conferencesSubtitle": "Monitor active, upcoming and ended conferences.",
  "page.revenueTitle": "Revenue",
  "page.revenueSubtitle":
    "Track sponsorships, invoices, payments and other revenue.",
  "page.expensesTitle": "Expenses",
  "page.expensesSubtitle":
    "Record and review actual conference spending by category.",
  "page.adminAccessRequired":
    "System administrator access is required for this page.",
  "expense.heading": "Conference expenses",
  "expense.description":
    "Actual spending recorded against this conference. Reversed entries remain visible for audit purposes.",
  "expense.conference": "Conference",
  "expense.form.add": "Add expense",
  "expense.form.heading": "Record an expense",
  "expense.form.category": "Expense type",
  "expense.form.title": "Expense name",
  "expense.form.titleHint": "For example: venue operations or speaker travel",
  "expense.form.amount": "Amount",
  "expense.form.date": "Expense date",
  "expense.form.record": "Record expense",
  "expense.form.invalid": "Enter a valid name, amount and date.",
  "expense.form.recorded": "Expense recorded successfully.",
  "expense.form.failed": "The expense could not be recorded. Try again.",
  "expense.category.operations": "Operations",
  "expense.category.marketing": "Marketing",
  "expense.category.hospitality": "Hospitality",
  "expense.category.technology": "Technology",
  "expense.category.speakers": "Speakers",
  "expense.category.travelAccommodation": "Travel and accommodation",
  "expense.category.equipment": "Equipment",
  "expense.category.other": "Other",
  "expense.summary.aria": "Active expense summary",
  "expense.summary.total": "Total spent",
  "expense.summary.count": "Active entries",
  "expense.loading": "Loading expenses…",
  "expense.loadFailed": "Expenses could not be loaded.",
  "expense.unavailableShort": "Unavailable",
  "expense.unavailable.heading": "Expenses unavailable",
  "expense.unavailable.description":
    "The conference list could not be loaded. Try again later.",
  "expense.noConferences.heading": "No conferences available",
  "expense.noConferences.description":
    "Connect a conference before recording expenses.",
  "expense.empty.heading": "No expenses recorded",
  "expense.empty.description": "Recorded conference expenses will appear here.",
  "expense.column.title": "Expense",
  "expense.column.category": "Type",
  "expense.column.date": "Date",
  "expense.column.amount": "Amount",
  "expense.column.status": "Status",
  "expense.column.action": "Action",
  "expense.status.active": "Active",
  "expense.status.reversed": "Reversed",
  "expense.edit.action": "Edit",
  "expense.edit.heading": "Edit expense",
  "expense.edit.description":
    "Update the active expense record. The change is recorded for audit.",
  "expense.edit.close": "Close expense editor",
  "expense.edit.save": "Save changes",
  "expense.edit.invalid": "Enter a valid name, amount and date.",
  "expense.edit.recorded": "Expense updated successfully.",
  "expense.edit.failed":
    "The expense could not be updated. Refresh and try again.",
  "expense.reverse.action": "Reverse entry",
  "expense.reverse.confirm":
    "Reverse this expense? The original entry will remain in the audit record.",
  "expense.reverse.busy": "Reversing…",
  "expense.reverse.recorded": "Reversed",
  "expense.reverse.failed": "The expense could not be reversed. Try again.",
  "page.connectTitle": "Connect conference",
  "page.connectSubtitle":
    "Retrieve conference data from its Firebase project, then review the connection.",
  "page.usersTitle": "Users",
  "page.usersSubtitle": "Viewer access and administrative audit events.",
  "page.plannedTitle": "Planned workspace",
  "page.plannedBody":
    "This area will become available in its approved execution step.",
  "page.returnOverview": "Return to overview",
} as const satisfies Record<string, MessageDefinition>;

export type MessageKey = keyof typeof ENGLISH_MESSAGES;

export type TranslationCatalog = Readonly<
  Record<MessageKey, MessageDefinition>
>;

const ARABIC_MESSAGES = {
  "collections.title": "التحصيل اليومي لرسوم التسجيل",
  "collections.range": "الفترة",
  "collections.all": "من أول عملية مؤرخة",
  "collections.last30": "آخر 30 يومًا",
  "collections.last90": "آخر 90 يومًا",
  "collections.count": "الفواتير المسددة",
  "collections.amount": "المبلغ المحصّل",
  "collections.period": "الفترة المعروضة · توقيت السعودية",
  "collections.date": "التاريخ",
  "collections.table": "عرض جدول البيانات اليومية",
  "collections.description":
    "فواتير ميسر لرسوم التسجيل فقط، حسب تاريخ عملية الدفع الناجحة المحفوظ في مشروع المؤتمر بتوقيت السعودية. تستبعد الفواتير المستردة والملغاة والتحويلات البنكية والرعايات والإيرادات الأخرى.",
  "collections.help":
    "مرّر المؤشر أو اضغط على يوم لعرض العدد والمبلغ، أو استخدم لوحة المفاتيح. تُعرض الأيام دون تحصيل بصفر. اسحب أفقيًا لمشاهدة كامل الفترة.",
  "collections.missing":
    "عدد {count} من الفواتير المسددة لا يتوفر لها تاريخ دفع موثوق، لذلك لا تدخل في الرسم. مبلغها:",
  "collections.stale":
    "تُعرض بيانات آخر مزامنة ناجحة؛ قد لا تكون أرقام اليوم مكتملة.",
  "collections.unavailable":
    "تواريخ التحصيل اليومية غير متاحة بعد في آخر بيانات المؤتمر.",
  "collections.empty": "لا توجد فواتير تسجيل مسددة حتى الآن.",
  "collections.noDates":
    "سيظهر الرسم عند توفر تواريخ موثوقة لعمليات الدفع الناجحة. لا تُستخدم تواريخ المزامنة بديلًا عنها.",
  "app.documentTitle": "لوحة المتابعة التنفيذية لمؤتمرات جامعة طيبة",
  "app.name": "مؤتمرات جامعة طيبة",
  "locale.arabic": "العربية",
  "locale.english": "الإنجليزية",
  "locale.switchTo": "تغيير اللغة إلى {language}",
  "common.resultCount": {
    zero: "لا توجد نتائج",
    one: "نتيجة واحدة",
    two: "نتيجتان",
    few: "{count} نتائج",
    many: "{count} نتيجة",
    other: "{count} نتيجة",
  },
  "adminUsers.restricted.title": "إدارة المستخدمين",
  "adminUsers.restricted.description": "يلزم توفر صلاحية المسؤول.",
  "adminUsers.notice.loadingViewers": "جارٍ تحميل المستخدمين…",
  "adminUsers.notice.directoryReady": "قائمة المستخدمين جاهزة",
  "adminUsers.notice.creating": "جارٍ إرسال الدعوة…",
  "adminUsers.notice.restoring": "جارٍ استعادة وصول المستخدم…",
  "adminUsers.notice.disabling": "جارٍ تعطيل المستخدم…",
  "adminUsers.notice.disabled": "تم تعطيل وصول المستخدم.",
  "adminUsers.notice.alreadyActiveAvailable":
    "نشط بالفعل — يمكن للمستخدم الوصول.",
  "adminUsers.notice.pendingRestored":
    "تمت استعادة الوصول وقبول رسالة الإعداد للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد.",
  "adminUsers.notice.resetting": "جارٍ إعادة ضبط وصول المستخدم…",
  "adminUsers.notice.resetSentActive":
    "أُلغيت الجلسات الحالية وقُبلت رسالة إعادة الضبط للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد.",
  "adminUsers.notice.resetSentPending":
    "الإعداد معلّق. قُبلت رسالة إعادة الضبط للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد.",
  "adminUsers.notice.resending": "جارٍ إرسال الدعوة…",
  "adminUsers.notice.alreadyComplete": "نشط بالفعل — اكتمل الإعداد مسبقًا.",
  "adminUsers.notice.newSetupSent":
    "قُبلت الدعوة للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد. ما زال على المستخدم إكمال الإعداد.",
  "adminUsers.notice.loadingAudit": "جارٍ تحميل أحداث التدقيق…",
  "adminUsers.notice.auditLoaded": "تم تحميل أحداث التدقيق",
  "adminUsers.creation.created":
    "قُبلت الدعوة للإرسال. يختار المستخدم كلمة مروره، ثم يسجّل الدخول ويُعدّ تطبيق المصادقة. لم يُؤكّد وصول الرسالة إلى صندوق الوارد.",
  "adminUsers.creation.pending":
    "الإعداد معلّق. قُبلت الدعوة للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد. يمكنك إعادة إرسال الدعوة عند الحاجة.",
  "adminUsers.creation.notSent":
    "الحساب معلّق، لكن الدعوة لم تُرسل. اختر إرسال الدعوة بجوار المستخدم.",
  "adminUsers.creation.active":
    "نشط بالفعل — لدى هذا المستخدم وصول إلى اللوحة.",
  "adminUsers.creation.recoveredActive":
    "نشط بالفعل — تمت استعادة وصول المستخدم الحالي.",
  "adminUsers.creation.recoveredPending":
    "تمت استعادة الإعداد المعلّق وقبول الدعوة للإرسال، ولم يُؤكّد وصولها إلى صندوق الوارد.",
  "adminUsers.error.invalidEmail": "أدخل عنوان بريد إلكتروني صالحًا.",
  "adminUsers.error.invalidPassword":
    "استخدم من 14 إلى 128 محرفًا، مع حرف إنجليزي كبير وصغير ورقم ورمز.",
  "adminUsers.error.notFound": "تعذر العثور على حساب المستخدم. حدّث القائمة.",
  "adminUsers.error.protectedIdentity":
    "لا يمكن تحويل هذه الهوية المحمية إلى مستخدم قارئ.",
  "adminUsers.error.identityConflict":
    "هذا البريد مرتبط بهوية تسجيل دخول قائمة، ولا يمكن تحويلها إلى مستخدم قارئ.",
  "adminUsers.error.profileConflict":
    "يحتاج هذا الحساب إلى مراجعة المسؤول قبل متابعة الإعداد.",
  "adminUsers.error.disabled":
    "هذا المستخدم معطّل. استعد الوصول قبل إعادة إرسال الإعداد.",
  "adminUsers.error.provisioning":
    "إعداد المستخدم قيد التنفيذ. حدّث القائمة وحاول بعد قليل.",
  "adminUsers.error.deliveryFailed":
    "تعذر إرسال الدعوة. ما زال الحساب معلّقًا. اختر إعادة إرسال الدعوة للمحاولة مجددًا.",
  "adminUsers.error.identityPermission":
    "إدارة المستخدمين غير مهيأة بصورة صحيحة. تواصل مع مسؤول النظام.",
  "adminUsers.error.identityUnavailable":
    "خدمة تسجيل الدخول غير متاحة مؤقتًا. حاول مجددًا.",
  "adminUsers.error.profileUnavailable":
    "قائمة المستخدمين غير متاحة مؤقتًا. حاول مجددًا.",
  "adminUsers.error.auditUnavailable":
    "تغيّر الحساب، لكن تعذر تأكيد سجل التدقيق. حدّث القائمة قبل إعادة المحاولة.",
  "adminUsers.error.disableFailed": "تعذر تعطيل المستخدم. حاول مجددًا.",
  "adminUsers.error.resetFailed": "تعذرت استعادة وصول المستخدم. حاول مجددًا.",
  "adminUsers.error.permissionDenied":
    "يلزم توفر صلاحية المسؤول لإدارة المستخدمين.",
  "adminUsers.error.sessionExpired":
    "انتهت جلستك الآمنة. سجّل الدخول مجددًا للمتابعة.",
  "adminUsers.error.network": "تعذر الوصول إلى الخدمة. حاول مجددًا.",
  "adminUsers.error.service": "إدارة المستخدمين غير متاحة مؤقتًا.",
  "adminUsers.error.invalidResponse":
    "أعادت إدارة المستخدمين استجابة غير صالحة.",
  "adminUsers.error.request": "تعذر إكمال الطلب. حاول مجددًا.",
  "adminUsers.error.withRefreshGuidance":
    "{message} أعد تحميل الصفحة لتحديث قائمة المستخدمين قبل المحاولة مجددًا.",
  "adminUsers.heading": "إعداد الحسابات وتسجيل الدخول",
  "adminUsers.description":
    "أضف المستخدمين وتابع إعداد حساباتهم وأعد إرسال تعليمات الدخول. تُدار الأدوار وصلاحيات المؤتمرات في القسم أعلاه.",
  "adminUsers.create": "دعوة مستخدم",
  "adminUsers.summary.aria": "ملخص حسابات المستخدمين",
  "adminUsers.summary.active": "المستخدمون النشطون",
  "adminUsers.summary.disabled": "المستخدمون المعطّلون",
  "adminUsers.summary.pending": "الإعداد المعلّق",
  "adminUsers.summary.deliveryFailed": "فشل الإرسال",
  "adminUsers.filter.label": "تصفية المستخدمين",
  "adminUsers.filter.all": "جميع المستخدمين",
  "adminUsers.filter.active": "المستخدمون النشطون",
  "adminUsers.filter.pending": "الإعداد المعلّق",
  "adminUsers.filter.deliveryFailed": "فشل الإرسال",
  "adminUsers.filter.disabled": "المستخدمون المعطّلون",
  "adminUsers.table.caption": "حسابات المستخدمين في اللوحة",
  "adminUsers.table.user": "المستخدم",
  "adminUsers.table.role": "الدور",
  "adminUsers.table.status": "الحالة",
  "adminUsers.table.lastSignIn": "آخر تسجيل دخول",
  "adminUsers.table.actions": "الإجراءات",
  "adminUsers.role.viewer": "قارئ",
  "adminUsers.status.active": "نشط",
  "adminUsers.status.pending": "الإعداد معلّق",
  "adminUsers.status.notSent": "الدعوة لم تُرسل",
  "adminUsers.status.deliveryFailed": "فشل الإرسال",
  "adminUsers.status.disabled": "معطّل",
  "adminUsers.notSignedIn": "لم يسجّل الدخول بعد",
  "adminUsers.action.working": "جارٍ التنفيذ…",
  "adminUsers.action.restore": "استعادة الوصول",
  "adminUsers.action.disable": "تعطيل",
  "adminUsers.action.resetting": "جارٍ إعادة الضبط…",
  "adminUsers.action.reset": "إعادة ضبط الوصول",
  "adminUsers.action.resending": "جارٍ الإرسال…",
  "adminUsers.action.send": "إرسال الدعوة",
  "adminUsers.action.resend": "إعادة إرسال الدعوة",
  "adminUsers.empty": "لا يوجد مستخدمون يطابقون هذه التصفية.",
  "adminUsers.audit.heading": "أحداث التدقيق الإداري",
  "adminUsers.audit.refresh": "تحديث أحداث التدقيق",
  "adminUsers.audit.action.viewerCreated": "إنشاء مستخدم قارئ",
  "adminUsers.audit.action.userDeletionStarted": "بدء حذف المستخدم",
  "adminUsers.audit.action.userDeleted": "حذف المستخدم نهائيًا",
  "adminUsers.audit.action.userDeletionDenied": "رفض حذف المستخدم",
  "adminUsers.audit.action.userDeletionFailed": "حذف المستخدم غير مكتمل",
  "adminUsers.audit.action.viewerReconciled": "تسوية حالة المستخدم",
  "adminUsers.audit.action.mfaActivated": "تفعيل التحقق متعدد العوامل",
  "adminUsers.audit.action.setupResent": "إعادة إرسال إعداد المستخدم",
  "adminUsers.audit.action.viewerDisabled": "تعطيل المستخدم",
  "adminUsers.audit.action.accessReset": "إعادة ضبط وصول المستخدم",
  "adminUsers.audit.action.managementDenied": "رفض إدارة المستخدم",
  "adminUsers.audit.action.signIn": "تسجيل الدخول",
  "adminUsers.audit.action.signOut": "تسجيل الخروج",
  "adminUsers.audit.action.mfaEnrolled": "إعداد المصادقة متعددة العوامل",
  "adminUsers.audit.action.signInDenied": "رفض تسجيل الدخول",
  "adminUsers.audit.action.conferenceDisabled": "تعطيل المؤتمر",
  "adminUsers.audit.action.conferenceDiscovered": "اكتشاف المؤتمر",
  "adminUsers.audit.action.conferenceRegistrationRequested":
    "طلب تسجيل المؤتمر",
  "adminUsers.audit.action.conferenceRegistrationActivated":
    "تفعيل تسجيل المؤتمر",
  "adminUsers.audit.action.conferenceManagementDenied": "رفض إدارة المؤتمر",
  "adminUsers.audit.action.brandingUploaded": "رفع هوية المؤتمر",
  "adminUsers.audit.action.brandingRemoved": "إزالة هوية المؤتمر",
  "adminUsers.audit.action.brandingManagementDenied": "رفض إدارة هوية المؤتمر",
  "adminUsers.audit.action.sponsorshipCreated": "إنشاء رعاية",
  "adminUsers.audit.action.sponsorshipUpdated": "تحديث رعاية",
  "adminUsers.audit.action.sponsorshipInvoiceRecorded": "تسجيل فاتورة رعاية",
  "adminUsers.audit.action.sponsorshipPaymentRecorded": "تسجيل دفعة رعاية",
  "adminUsers.audit.action.sponsorshipManagementDenied": "رفض إدارة الرعاية",
  "adminUsers.audit.action.otherRevenueCreated": "إنشاء إيراد آخر",
  "adminUsers.audit.action.otherRevenueUpdated": "تحديث إيراد آخر",
  "adminUsers.audit.action.otherRevenueReversed": "عكس إيراد آخر",
  "adminUsers.audit.action.otherRevenueManagementDenied":
    "رفض إدارة الإيرادات الأخرى",
  "adminUsers.audit.action.ledgerEntryCreated": "إنشاء قيد دفتر أستاذ",
  "adminUsers.audit.action.ledgerEntryReversed": "عكس قيد دفتر أستاذ",
  "adminUsers.audit.action.other": "نشاط إداري",
  "adminUsers.audit.outcome.success": "نجح",
  "adminUsers.audit.outcome.denied": "مرفوض",
  "adminUsers.audit.outcome.failed": "فشل",
  "adminUsers.drawer.description":
    "أدخل البريد الإلكتروني فقط. تتيح الدعوة للمستخدم اختيار كلمة مروره، ثم تسجيل الدخول وإعداد Google Authenticator قبل الوصول إلى اللوحة.",
  "adminUsers.drawer.close": "إغلاق نافذة دعوة المستخدم",
  "adminUsers.drawer.email": "البريد الإلكتروني للمستخدم",
  "adminUsers.drawer.deliveryHint":
    "لا تُشارك كلمة المرور مع المسؤول ولا تُرسل بالبريد. تصل الدعوة باللغة العربية دائمًا. حالة الإرسال لا تعني تأكيد وصول الرسالة إلى صندوق الوارد.",
  "adminUsers.drawer.access": "الوصول",
  "adminUsers.drawer.readOnly":
    "لا تظهر مؤتمرات حتى يمنحه المسؤول صلاحية الوصول",
  "adminUsers.drawer.cancel": "إلغاء",
  "adminUsers.drawer.sending": "جارٍ الإرسال…",
  "adminUsers.drawer.send": "إرسال الدعوة",
  "finance.method.bankTransfer": "تحويل بنكي",
  "finance.method.bankDeposit": "إيداع بنكي",
  "finance.method.other": "أخرى",
  "finance.field.reference": "مرجع التحويل أو الإيصال",
  "finance.field.evidence": "مرجع مستند الإثبات",
  "finance.field.referenceHint":
    "أدخل مرجع التحويل أو الإيصال، مثل PAY-2026-001، دون بيانات شخصية.",
  "finance.field.evidenceHint":
    "أدخل معرّف مستند الإثبات في سجلاتكم، مثل DOC-001. تحفظ هذه الخانة مرجعًا فقط ولا ترفع ملفًا.",
  "finance.field.referenceInvalid":
    "استخدم من 2 إلى 160 حرفًا إنجليزيًا أو رقمًا، مع . _ : - عند الحاجة. ابدأ بحرف أو رقم، دون مسافات أو بيانات شخصية.",
  "finance.field.localTimeHint":
    "التاريخ والوقت حسب المنطقة الزمنية المحلية لجهازك.",
  "finance.form.requiredHint": "جميع الحقول مطلوبة.",
  "finance.form.contextLocked":
    "احفظ الإدخال الحالي أو ألغِه قبل اختيار مؤتمر آخر.",
  "finance.form.leaveConfirm":
    "لديك تغييرات لم تُحفظ. هل تريد مغادرة الصفحة وتجاهلها؟",
  "finance.form.referencesHeading": "المراجع والإثبات",
  "finance.form.cancel": "إلغاء الإدخال",
  "finance.field.method": "الطريقة",
  "finance.field.status": "الحالة",
  "finance.placeholder.amount": "0.00",
  "finance.action.tryAgain": "المحاولة مجددًا",
  "finance.action.saving": "جارٍ الحفظ…",
  "otherRevenue.category.exhibition": "المعرض",
  "otherRevenue.category.workshop": "ورشة عمل",
  "otherRevenue.category.grant": "منحة",
  "otherRevenue.category.donation": "تبرع",
  "otherRevenue.category.service": "خدمة",
  "otherRevenue.category.other": "أخرى",
  "otherRevenue.status.active": "نشط",
  "otherRevenue.status.reversed": "ملغى ماليًا",
  "otherRevenue.form.invalid": "أدخل مبلغًا موجبًا وتاريخ تحصيل صالحين.",
  "otherRevenue.form.recorded": "تمت إضافة الإيراد.",
  "otherRevenue.form.failed": "تعذرت إضافة الإيراد. راجع الحقول وحاول مجددًا.",
  "otherRevenue.form.add": "إضافة إيراد آخر",
  "otherRevenue.form.source": "الجهة الدافعة",
  "otherRevenue.form.category": "الفئة",
  "otherRevenue.form.description": "بيان الإيراد",
  "otherRevenue.form.detailsHeading": "تفاصيل الإيراد",
  "otherRevenue.form.amount": "المبلغ المحصّل ({currency})",
  "otherRevenue.form.amountLabel": "المبلغ المحصّل",
  "otherRevenue.optional": "اختياري",
  "otherRevenue.actions": "الإجراءات",
  "otherRevenue.conferenceHeading": "إيرادات المؤتمر",
  "otherRevenue.form.requiredHint":
    "المبلغ المحصّل وبيان الإيراد إلزاميان. مرجع التحويل أو الإيصال ومرجع مستند الإثبات اختياريان.",
  "otherRevenue.edit.action": "تعديل",
  "otherRevenue.edit.heading": "تعديل الإيراد",
  "otherRevenue.edit.save": "حفظ التعديل",
  "otherRevenue.edit.saved": "تم تعديل الإيراد وتحديث الإجماليات.",
  "otherRevenue.edit.failed":
    "تعذر حفظ التعديل. قد يكون السجل عُدّل أو أُلغي. ألغِ التعديل وحدّث الصفحة ثم حاول مجددًا.",
  "otherRevenue.form.collectionMethod": "طريقة التحصيل",
  "otherRevenue.form.collectedAt": "تاريخ التحصيل",
  "otherRevenue.form.record": "إضافة الإيراد",
  "otherRevenue.heading": "الإيرادات الأخرى",
  "otherRevenue.description":
    "مبالغ محصّلة من مصادر أخرى، مثل المعارض وورش العمل والمنح والتبرعات. تُسجّل رسوم التسجيل والرعايات في أقسامها الخاصة.",
  "otherRevenue.loading": "جارٍ تحميل الإيرادات الأخرى…",
  "otherRevenue.loadFailed": "تعذر تحميل الإيرادات الأخرى.",
  "otherRevenue.empty": "لم تُسجل أي إيرادات أخرى.",
  "otherRevenue.reverse.heading": "إلغاء الإيراد",
  "otherRevenue.reverse.description":
    "سيُستبعد المبلغ من الإجماليات، مع الاحتفاظ بالسجل الأصلي وقيد إلغاء مطابق.",
  "otherRevenue.reverse.busy": "جارٍ إلغاء الإيراد…",
  "otherRevenue.reverse.confirm": "تأكيد الإلغاء",
  "otherRevenue.reverse.failed": "تعذر إلغاء الإيراد. حاول مجددًا.",
  "otherRevenue.reverse.recorded":
    "أُلغي الإيراد مع الاحتفاظ بالسجل الأصلي وقيد الإلغاء.",
  "sponsorship.form.invalidMovement": "أدخل مبلغًا موجبًا وتاريخًا صالحين.",
  "sponsorship.form.invoiceRecorded": "تم تسجيل الفاتورة.",
  "sponsorship.form.paymentRecorded": "تم تسجيل الدفعة.",
  "sponsorship.form.invoiceFailed":
    "تعذر تسجيل الفاتورة. راجع الجزء من قيمة الرعاية الذي لم تصدر له فواتير بعد.",
  "sponsorship.form.paymentFailed":
    "تعذر تسجيل الدفعة. راجع رصيد الفواتير غير المسدّد.",
  "sponsorship.form.recordInvoice": "تسجيل فاتورة",
  "sponsorship.form.recordPayment": "تسجيل دفعة",
  "sponsorship.form.amount": "المبلغ ({currency})",
  "sponsorship.form.amountLabel": "المبلغ",
  "sponsorship.form.valueLabel": "قيمة الرعاية",
  "sponsorship.form.paymentMethod": "طريقة الدفع",
  "sponsorship.form.invoiceReference": "رقم الفاتورة",
  "sponsorship.form.invoiceReferenceHint":
    "أدخل رقم الفاتورة، مثل INV-2026-001، دون بيانات شخصية.",
  "sponsorship.form.paymentReference": "مرجع التحويل أو الإيصال",
  "sponsorship.form.referenceHint":
    "أدخل مرجع الرعاية في سجلاتكم، مثل SP-2026-001، دون بيانات شخصية.",
  "sponsorship.form.detailsHeading": "تفاصيل الرعاية",
  "sponsorship.form.issuedAt": "تاريخ الإصدار",
  "sponsorship.form.receivedAt": "تاريخ الاستلام",
  "sponsorship.form.saveInvoice": "حفظ الفاتورة",
  "sponsorship.form.savePayment": "حفظ الدفعة",
  "sponsorship.form.invalidContract": "أدخل قيمة رعاية أكبر من صفر.",
  "sponsorship.form.created": "تمت إضافة الرعاية.",
  "sponsorship.form.createFailed":
    "تعذرت إضافة الرعاية. راجع الحقول وحاول مجددًا.",
  "sponsorship.form.add": "إضافة رعاية",
  "sponsorship.form.organization": "الجهة الراعية",
  "sponsorship.form.package": "الباقة",
  "sponsorship.form.packagePlaceholder": "بلاتينية، ذهبية، مخصصة…",
  "sponsorship.form.contractValue": "قيمة الرعاية ({currency})",
  "sponsorship.form.agreementReference": "مرجع الرعاية",
  "sponsorship.form.creating": "جارٍ الإضافة…",
  "sponsorship.form.create": "إضافة رعاية",
  "sponsorship.unavailable.heading": "الرعايات غير متاحة",
  "sponsorship.unavailable.description":
    "تعذر تحميل بيانات الرعايات. حاول مجددًا.",
  "sponsorship.management.aria": "إدارة الرعايات",
  "sponsorship.conference": "المؤتمر",
  "sponsorship.totals.aria": "إجماليات الرعايات",
  "sponsorship.metric.contracted": "قيمة الرعايات",
  "sponsorship.metric.invoiced": "قيمة الفواتير الصادرة",
  "sponsorship.metric.collected": "المبالغ المحصّلة",
  "sponsorship.metric.outstanding": "المتبقي من قيمة الرعاية",
  "sponsorship.loading": "جارٍ تحميل الرعايات…",
  "sponsorship.empty.heading": "لا توجد رعايات مسجّلة",
  "sponsorship.empty.description":
    "أضف رعاية لبدء متابعة قيمتها وفواتيرها ودفعاتها.",
  "sponsorship.evidence": "مستند الإثبات:",
  "connect.capability.automated": "آلي",
  "connect.capability.unavailable": "غير متاح",
  "connect.manifestDiscovered": "تم العثور على بيان الموصل",
  "connect.metadataLocked":
    "تُستمد بيانات المؤتمر من بيان الموصل الخاص ولا يمكن تعديلها من هنا.",
  "connect.field.projectId": "معرّف المشروع",
  "connect.field.conferenceKey": "مفتاح المؤتمر",
  "connect.field.starts": "يبدأ",
  "connect.field.ends": "ينتهي",
  "connect.field.timezone": "المنطقة الزمنية",
  "connect.field.connectorRegion": "منطقة الموصل",
  "connect.field.connectorVersion": "إصدار الموصل",
  "connect.field.adapterVersion": "إصدار المهايئ",
  "connect.field.sourceVersion": "إصدار المصدر",
  "connect.objective.title": "هدف المؤتمر",
  "connect.objective.empty": "لم يزوّد الموصل هدفًا للمؤتمر.",
  "connect.coverage.title": "تغطية البيانات الآلية",
  "connect.coverage.registrations": "التسجيلات",
  "connect.coverage.payments": "المدفوعات",
  "connect.coverage.analytics": "إحصاءات Google",
  "connect.activation.title": "تفعيل المسؤول",
  "connect.activation.body":
    "يجب أولًا أن يسجّل مسار المشروع هذه الهوية الموثقة نفسها بحالة معلّقة. والتفعيل إجراء مستقل خاضع للتدقيق.",
  "connect.activation.complete": "تم تفعيل المؤتمر",
  "connect.activation.activating": "جارٍ التفعيل…",
  "connect.activation.action": "تفعيل المؤتمر المعلّق",
  "connect.activation.error":
    "لم يُقبل التفعيل. تأكد من أن تسجيل المسار الموثق في حالة معلّقة.",
  "connect.discovery.kicker": "ربط بيانات المؤتمر",
  "connect.discovery.title": "أدخل معرّف مشروع Firebase",
  "connect.discovery.body":
    "يوفر الموصل اسم المؤتمر وشعاره وهدفه وتواريخه تلقائيًا.",
  "connect.discovery.projectPlaceholder": "conference-2027",
  "connect.discovery.manualOnly":
    "أدخل معرّف المشروع، وليس اسم المؤتمر أو رابط موقعه.",
  "connect.discovery.discovering": "جارٍ جلب بيانات المؤتمر…",
  "connect.discovery.action": "جلب بيانات المؤتمر",
  "connect.discovery.sessionExpired":
    "انتهت جلستك. سجّل الدخول مجددًا للمتابعة.",
  "connect.discovery.notFound": "تعذر العثور على موصل موثوق لهذا المشروع.",
  "connect.preview.title": "معاينة المؤتمر",
  "connect.preview.empty":
    "أدخل معرّف المشروع ثم اختر «جلب بيانات المؤتمر» لعرض المعاينة.",
  "connect.status.planned": "مخطط له",
  "connect.status.active": "نشط",
  "connect.status.ended": "منتهٍ",
  "connect.status.archived": "مؤرشف",
  "branding.asset.icon": "أيقونة مربعة",
  "branding.asset.logo": "الشعار الكامل",
  "branding.asset.iconDescription":
    "تُستخدم في قوائم المؤتمرات والتنقل المختصر. اختر صورة شبه مربعة.",
  "branding.asset.logoDescription":
    "يُستخدم في رأس صفحة المؤتمر والعروض التفصيلية. يمكن استخدام أي نسبة أبعاد.",
  "branding.source.connector": "مصدر الموصل · لا يوجد تخصيص من المسؤول",
  "branding.source.admin":
    "رفع المسؤول · إصدار التخزين المؤقت {version} · {width} × {height} بكسل",
  "branding.previewAlt": "معاينة {asset} لمؤتمر {conferenceName}",
  "branding.checkingDimensions": "جارٍ التحقق من أبعاد الصورة…",
  "branding.error.dimensionsUnreadable":
    "تعذرت قراءة أبعاد الصورة. اختر ملفًا آخر.",
  "branding.error.previewUnavailable":
    "تعذر تجهيز معاينة الصورة. اختر ملفًا آخر.",
  "branding.updated": "تم تحديث {asset}. إصدار التخزين المؤقت الجديد نشط.",
  "branding.removed": "أزيل تخصيص {asset}. أصبح مصدر الموصل نشطًا.",
  "branding.title": "هوية المؤتمر",
  "conferenceEditor.open": "تعديل بيانات المؤتمر",
  "conferenceEditor.title": "اسم المؤتمر باللغتين",
  "conferenceEditor.description":
    "حدّد الاسم الذي يظهر في كل لغة. تبقى هذه الأسماء محفوظة عند مزامنة بيانات المؤتمر. يمكنك تغيير الصور أدناه.",
  "conferenceEditor.ar": "الاسم بالعربية",
  "conferenceEditor.en": "الاسم بالإنجليزية",
  "conferenceEditor.preview": "معاينة الاسم",
  "conferenceEditor.save": "حفظ الاسمين",
  "conferenceEditor.saving": "جارٍ الحفظ…",
  "conferenceEditor.cancel": "تجاهل التعديلات",
  "conferenceEditor.saved": "تم حفظ اسمي المؤتمر.",
  "conferenceEditor.error":
    "تعذّر حفظ الاسمين. احتفظنا بما أدخلته؛ حاول مرة أخرى.",
  "conferenceEditor.invalid":
    "أدخل الاسمين بحد أقصى 160 حرفًا لكل اسم، دون وسوم أو رموز تحكم.",
  "conferenceEditor.unsaved": "لديك تعديلات لم تُحفظ بعد.",
  "conferenceWebsite.title": "الموقع الرسمي للمؤتمر",
  "conferenceWebsite.label": "رابط الموقع الإلكتروني",
  "conferenceWebsite.description":
    "أدخل رابط HTTPS كاملًا، بما فيه النطاق الخاص بالمؤتمر. احفظ الموقع بشكل مستقل عن الاسمين والصور. اترك الحقل فارغًا واحفظ لاستعادة الموقع الافتراضي.",
  "conferenceWebsite.default": "الموقع الافتراضي:",
  "conferenceWebsite.save": "حفظ الموقع",
  "conferenceWebsite.saved": "تم حفظ الموقع الرسمي للمؤتمر.",
  "conferenceWebsite.reset": "تمت استعادة الموقع الافتراضي للمؤتمر.",
  "conferenceWebsite.error":
    "تعذّر حفظ الموقع. احتفظنا بما أدخلته؛ حاول مرة أخرى.",
  "conferenceWebsite.invalid":
    "أدخل رابط HTTPS كاملًا بنطاق عام، دون بيانات تسجيل دخول أو رموز تحكم. اترك الحقل فارغًا لاستعادة الموقع الافتراضي.",
  "branding.description":
    "عاين الأيقونة المختصرة والشعار الكامل المستخدمين في لوحة المتابعة التنفيذية.",
  "branding.access.admin": "أدوات المسؤول",
  "branding.access.readOnly": "للقراءة فقط",
  "branding.readyToUpload": "جاهز للرفع · {size} ميبيبايت",
  "branding.chooseImage": "اختيار صورة",
  "branding.uploading": "جارٍ الرفع…",
  "branding.upload": "رفع",
  "branding.removing": "جارٍ الحذف…",
  "branding.removeOverride": "إزالة التخصيص",
  "branding.constraints":
    "PNG أو JPEG أو WebP أو SVG آمن · بحد أقصى {maxMiB} ميبيبايت · من {minPixels} إلى {maxPixels} بكسل لكل ضلع · بحد أقصى {maxMegapixels} ميغابكسل. يجب أن تكون الأيقونة شبه مربعة بنسبة أبعاد من {minAspect} إلى {maxAspect}. تؤدي إزالة التخصيص إلى استعادة مصدر الموصل.",
  "branding.viewerNote":
    "يمكن للمشاهدين الاطلاع على إصدارات الهوية، ولا يمكنهم رفع الأصول أو حذفها.",
  "branding.error.fileTooLarge":
    "اختر صورة لا يزيد حجمها على {maxMiB} ميبيبايت.",
  "branding.error.unsupportedType": "اختر صورة PNG أو JPEG أو WebP أو SVG.",
  "branding.error.iconAspect":
    "اختر أيقونة شبه مربعة بنسبة أبعاد بين {minAspect} و{maxAspect}.",
  "branding.error.invalidDimensions":
    "اختر صورة أبعادها بين {minPixels} و{maxPixels} بكسل لكل ضلع ولا تتجاوز {maxMegapixels} ميغابكسل.",
  "branding.error.emptyFile": "ملف الصورة المحدد فارغ. اختر ملفًا آخر.",
  "branding.error.uploadLimit":
    "تتجاوز الصورة حد الرفع البالغ {maxMiB} ميبيبايت.",
  "branding.error.invalidImage":
    "رُفضت الصورة. تحقق من تنسيقها وأبعادها ومحتوى SVG الآمن، ثم حاول مجددًا.",
  "branding.error.conferenceNotFound":
    "لم يعد هذا المؤتمر متاحًا. حدّث الصفحة قبل المحاولة مجددًا.",
  "branding.error.storageUnavailable":
    "تخزين الهوية غير متاح مؤقتًا. حاول لاحقًا.",
  "branding.error.sessionExpired":
    "انتهت جلستك. سجّل الدخول مجددًا قبل تغيير هوية المؤتمر.",
  "branding.error.saveFailed": "تعذر حفظ تغيير الهوية. حاول مجددًا.",
  "map.title": "الزوار حسب الدولة · آخر 30 يومًا",
  "map.visitorAria": {
    zero: "{country}: لا زوار خلال آخر 30 يومًا",
    one: "{country}: زائر واحد خلال آخر 30 يومًا",
    two: "{country}: زائران خلال آخر 30 يومًا",
    few: "{country}: {count} زوار خلال آخر 30 يومًا",
    many: "{country}: {count} زائرًا خلال آخر 30 يومًا",
    other: "{country}: {count} زائر خلال آخر 30 يومًا",
  },
  "map.description":
    "خريطة عالمية متدرجة تعرض الزوار للدول التي تتوفر لها حدود على الخريطة. وترد بعد الخريطة البيانات الكاملة للدول وبيانات التحليلات غير المطابقة.",
  "map.visitorCount": {
    zero: "لا زوار",
    one: "زائر واحد",
    two: "زائران",
    few: "{count} زوار",
    many: "{count} زائرًا",
    other: "{count} زائر",
  },
  "map.legend.lower": "أقل",
  "map.legend.higher": "أعلى",
  "map.allCountryData": {
    zero: "كل بيانات الدول (لا زوار)",
    one: "كل بيانات الدول (زائر واحد)",
    two: "كل بيانات الدول (زائران)",
    few: "كل بيانات الدول ({count} زوار)",
    many: "كل بيانات الدول ({count} زائرًا)",
    other: "كل بيانات الدول ({count} زائر)",
  },
  "map.boundaryUnavailable": "الحدود غير متاحة",
  "map.noRecognizedCountry":
    "لا تتوفر بيانات مجمعة لدولة معروفة خلال هذه الفترة.",
  "map.unmappedTitle": "بيانات تحليلات غير مرتبطة بالخريطة",
  "map.emptyCountryValue": "قيمة الدولة فارغة",
  "map.none": "لا يوجد.",
  "detail.loading": "جارٍ تحميل تفاصيل المؤتمر",
  "detail.error.title": "تفاصيل المؤتمر غير متاحة",
  "detail.error.description": "تعذر تحميل السجل المجمّع للمؤتمر.",
  "detail.breadcrumb.conferences": "المؤتمرات",
  "detail.website.open": "فتح موقع المؤتمر",
  "detail.website.ariaLabel": "فتح موقع مؤتمر {conference}",
  "detail.lastSynchronized": "آخر مزامنة {date}",
  "detail.notice.upgradeTitle": "يلزم تحديث الموصل",
  "detail.notice.upgradeDescription":
    "تظل أحدث البيانات المجمّعة الآمنة ظاهرة بينما يحدّث المسؤول موصل هذا المؤتمر.",
  "detail.notice.delayedTitle": "تأخر تحديث البيانات",
  "detail.notice.delayedDescription":
    "تعرض المؤشرات بيانات آخر مزامنة مجمّعة ناجحة.",
  "detail.tabs.ariaLabel": "تفاصيل المؤتمر",
  "detail.tab.overview": "نظرة عامة",
  "detail.tab.analytics": "التحليلات",
  "detail.tab.finance": "المالية",
  "detail.tab.information": "المعلومات",
  "detail.trend.title": "اتجاه التسجيلات",
  "detail.trend.empty": "يظهر الاتجاه بعد نجاح لقطتين يوميتين.",
  "detail.trend.last30Days": "التسجيلات · آخر 30 يومًا",
  "detail.trend.ariaLabel": "اتجاه إجمالي التسجيلات خلال آخر 30 يومًا",
  "detail.overview.title": "نظرة عامة على المؤتمر",
  "detail.overview.totalRegistrations": "إجمالي التسجيلات",
  "detail.overview.completed": "المكتملة",
  "detail.overview.pending": "قيد الانتظار",
  "detail.overview.awaitingPayment": "بانتظار الدفع",
  "detail.overview.incomplete": "تسجيلات غير مكتملة",
  "detail.overview.otherIncomplete": "تسجيلات أخرى غير مكتملة",
  "detail.overview.otherIncompleteHint":
    "التسجيلات الأخرى غير المكتملة لا تنتظر سداد فاتورة نشطة، مثل الفواتير المنتهية أو التسجيلات التي لم تصدر لها فاتورة.",
  "detail.overview.cancelled": "الملغاة",
  "detail.overview.financialSummary": "الملخص المالي",
  "detail.overview.registrationRevenueCollected": "إيرادات التسجيل المحصّلة",
  "detail.overview.invoicesIssued": "الفواتير الصادرة",
  "detail.overview.outstandingInvoices": "الفواتير المستحقة",
  "detail.overview.sponsorshipRevenue": "إيرادات الرعايات",
  "detail.overview.otherRevenue": "الإيرادات الأخرى",
  "detail.purpose.title": "الهدف",
  "detail.purpose.empty": "لم يزوّد مصدر المؤتمر هدفًا للمؤتمر.",
  "detail.dataStatus.title": "حالة البيانات",
  "detail.dataStatus.registration": "بيانات التسجيل",
  "detail.dataStatus.payment": "بيانات الدفع",
  "detail.dataStatus.analytics": "بيانات التحليلات",
  "detail.dataStatus.available": "متاحة",
  "detail.dataStatus.stale": "متأخرة",
  "detail.dataStatus.unavailable": "غير متاحة",
  "detail.invoiceStatus.notIssued": "لم تصدر فواتير",
  "detail.invoiceStatus.outstanding": "مستحقة",
  "detail.invoiceStatus.partiallyCollected": "محصّلة جزئيًا",
  "detail.invoiceStatus.collected": "محصّلة",
  "detail.finance.integrityKicker": "سلامة البيانات",
  "detail.finance.unavailableTitle": "البيانات المالية غير متاحة",
  "detail.finance.unavailableDescription":
    "تعذرت مطابقة المركز المالي على مستوى أصغر وحدة نقدية، لذلك لا تُعرض أي قيم جزئية أو تقديرية.",
  "detail.finance.executiveKicker": "العرض التنفيذي",
  "detail.finance.positionTitle": "ملخص التحصيل المالي",
  "detail.finance.currencyAria": "العملة {currency}",
  "detail.finance.collectedBasis": "على أساس المبالغ المحصّلة",
  "detail.finance.totalCollected": "إجمالي المبالغ المحصّلة",
  "detail.finance.pendingCollection": "مبالغ قيد التحصيل",
  "detail.finance.totalExpenses": "إجمالي المصروفات",
  "detail.finance.availableBalance": "الرصيد المتاح",
  "detail.finance.deficit": "عجز الميزانية",
  "detail.finance.expenseDetails": "تفاصيل المصروفات",
  "detail.finance.cashReceived": "المبالغ المستلمة",
  "detail.finance.cashReceivedDescription":
    "إجمالي ما تم استلامه فعليًا من رسوم التسجيل ودفعات الرعاية والإيرادات الأخرى. لا يشمل المبالغ التي لم تُسدّد بعد.",
  "detail.finance.componentsKicker": "حسب المصدر",
  "detail.finance.revenueMixTitle": "مصادر المبالغ المحصّلة",
  "detail.finance.revenueMixDescription":
    "توزيع المبالغ المستلمة فعليًا حسب مصدرها. لا تشمل هذه القيم فواتير غير مسدّدة أو رعايات لم تُحصّل بعد.",
  "detail.finance.registrationCollected": "رسوم التسجيل المحصّلة",
  "detail.finance.sponsorshipCollected": "دفعات الرعاية المحصّلة",
  "detail.finance.otherRevenueCollected": "الإيرادات الأخرى المحصّلة",
  "detail.finance.registrationOnlyKicker": "التسجيل فقط",
  "detail.finance.invoiceStatusTitle": "حالة الفواتير",
  "detail.finance.collectionRate": "نسبة التحصيل من قيمة الفواتير",
  "detail.finance.noInvoicesIssued": "لم تصدر فواتير تسجيل.",
  "detail.finance.collectionRateAria": "نسبة تحصيل فواتير التسجيل",
  "detail.finance.invoiceValueIssued": "قيمة فواتير التسجيل الصادرة",
  "detail.finance.outstandingBalance": "رصيد فواتير التسجيل المستحق",
  "detail.finance.invoicesIssued": "الفواتير الصادرة",
  "detail.finance.invoicesCollected": "الفواتير المحصّلة",
  "detail.finance.invoicesOutstanding": "الفواتير المستحقة",
  "detail.finance.invoiceNote":
    "هذه الأرقام لفواتير التسجيل فقط، ولا تشمل فواتير الرعاية. الرصيد المتبقي هو قيمة الفواتير الصادرة ناقص المبالغ المحصّلة.",
  "detail.information.title": "معلومات المؤتمر",
  "detail.information.conference": "المؤتمر",
  "detail.information.firebaseProject": "مشروع Firebase",
  "detail.information.starts": "يبدأ",
  "detail.information.ends": "ينتهي",
  "detail.information.connection": "الاتصال",
  "detail.information.lastSynchronized": "آخر مزامنة",
  "detail.analytics.dailyVisitors": "الزوار يوميًا",
  "detail.analytics.dailyEmpty":
    "الاتجاه اليومي في \u2066GA4\u2069 غير متاح بعد.",
  "detail.analytics.dailyAria":
    "اتجاه المستخدمين النشطين يوميًا خلال آخر 30 يومًا",
  "detail.analytics.unavailableTitle": "التحليلات غير متاحة مؤقتًا",
  "detail.analytics.unavailableDescription":
    "تظل بيانات التسجيل والبيانات المالية المجمّعة متاحة أثناء إعادة اتصال مصدر \u2066GA4\u2069.",
  "detail.analytics.audienceTitle": "المستخدمون النشطون حسب الفترة",
  "detail.analytics.today": "اليوم",
  "detail.analytics.yesterday": "أمس",
  "detail.analytics.thisMonth": "هذا الشهر",
  "detail.analytics.totalUsers": "إجمالي زوار الموقع",
  "detail.analytics.trackingPeriod": "البيانات المتاحة: من {start} إلى {end}",
  "detail.analytics.trackingSince": "أول بيانات متاحة: {start}",
  "detail.analytics.lifetimeUnavailable":
    "إجمالي الزوار للفترة المتاحة غير متاح حاليًا",
  "detail.analytics.lifetimeNote":
    "إجمالي مستخدمي الموقع في GA4 منذ أول بيانات متاحة، وليس مجموع الأعداد اليومية؛ فقد يزور المستخدم الموقع في أكثر من يوم.",
  "detail.analytics.last30Minutes": "آخر 30 دقيقة",
  "detail.analytics.estimateNote":
    "قد تتأخر تقديرات Google Analytics. تعرض هذه البطاقات المستخدمين النشطين لكل فترة، لا مرات مشاهدة الصفحات؛ أما الإجمالي أعلاه فيشمل جميع المستخدمين الذين سُجّل لهم حدث.",
  "detail.analytics.topCountries": "أكثر الدول زيارة",
  "detail.analytics.noCountries":
    "لا تتوفر بيانات مجمّعة للدول خلال هذه الفترة.",
  "common.unavailable": "غير متاح",
  "common.notApplicable": "لا ينطبق",
  "common.notSynchronized": "لم تتم المزامنة",
  "common.tryAgain": "إعادة المحاولة",
  "common.cancel": "إلغاء",
  "session.expired.title": "انتهت صلاحية جلستك",
  "session.expired.description":
    "سجّل الخروج ثم سجّل الدخول مجددًا للمتابعة بأمان.",
  "access.denied.title": "الوصول إلى اللوحة مقيّد",
  "access.error.accountInactive":
    "وصول حسابك إلى اللوحة غير نشط. تواصل مع مسؤول النظام لإكمال التفعيل أو استرداد الحساب.",
  "access.error.mfaEnrollmentRequired":
    "يلزم إكمال إعداد التحقق متعدد العوامل. سجّل الخروج ثم سجّل الدخول مجددًا لإكمال الإعداد. تواصل مع مسؤول النظام إذا استمر منع الوصول.",
  "access.error.mfaChallengeRequired":
    "يلزم التحقق متعدد العوامل. سجّل الخروج ثم سجّل الدخول مجددًا وأدخل رمز تطبيق المصادقة.",
  "access.error.providerNotAllowed":
    "طريقة تسجيل الدخول هذه غير مسموحة لحسابك. سجّل الخروج ثم استخدم طريقة الدخول المخصصة لك.",
  "access.error.profileNotFound":
    "لم يُهيّأ الوصول إلى اللوحة لحسابك. تواصل مع مسؤول النظام.",
  "access.error.unverifiedEmail":
    "تحقق من بريد حسابك الإلكتروني، ثم سجّل الخروج وسجّل الدخول مجددًا. تواصل مع مسؤول النظام إذا احتجت إلى مساعدة.",
  "access.error.permissionDenied":
    "ليست لديك صلاحية للوصول إلى هذه البيانات أو تنفيذ هذا الإجراء. تواصل مع مسؤول النظام إذا كنت بحاجة إلى الوصول.",
  "sync.notSynchronized": "لم تتم المزامنة",
  "sync.noSuccessfulSynchronization": "لا توجد مزامنة ناجحة",
  "sync.recently": "تمت المزامنة مؤخرًا",
  "sync.synchronizedRelative": "تمت المزامنة {relative}",
  "sync.dataMayBeDelayed": "قد تتأخر البيانات",
  "sync.sourcesFresh": "المصادر محدّثة",
  "sync.sourceDelay": "تأخر في المصدر",
  "conference.lifecycle.active": "نشط",
  "conference.lifecycle.upcoming": "قادم",
  "conference.lifecycle.ended": "منتهٍ",
  "conference.connection.connected": "متصل",
  "conference.connection.degraded": "متأخر",
  "conference.connection.neverSynchronized": "لم تتم المزامنة",
  "conference.connection.upgradeRequired": "يلزم التحديث",
  "conference.connection.disabled": "معطّل",
  "metric.totalConferences": "إجمالي المؤتمرات",
  "metric.totalConferences.definition": "جميع المؤتمرات المتاحة لهذا الحساب.",
  "metric.activeConferences": "المؤتمرات النشطة",
  "metric.activeConferences.definition":
    "المؤتمرات التي تشمل تواريخها تاريخ اليوم.",
  "metric.totalRegistrations": "إجمالي التسجيلات",
  "metric.totalRegistrations.definition":
    "جميع حالات التسجيل في أحدث لقطات المصادر.",
  "metric.netCollected": "صافي المحصّل",
  "metric.netCollected.definition":
    "رسوم التسجيل المحصّلة مضافًا إليها الرعايات والإيرادات الأخرى المستلمة.",
  "metric.outstandingInvoices": "الفواتير المستحقة",
  "metric.outstandingInvoices.definition":
    "فواتير التسجيل الصادرة التي لم تُسدّد بعد.",
  "metric.sponsorshipRevenue": "إيرادات الرعايات",
  "metric.sponsorshipRevenue.definition":
    "دفعات الرعاية المستلمة والمسجلة مركزيًا.",
  "metric.otherRevenue": "الإيرادات الأخرى",
  "metric.otherRevenue.definition":
    "الإيرادات الأخرى المستلمة والمسجلة مركزيًا.",
  "metric.visitorsThisMonth": "زوار هذا الشهر",
  "metric.visitorsThisMonth.definition":
    "تقدير المستخدمين النشطين في \u2066Google Analytics 4\u2069 للشهر الحالي.",
  "table.conference": "المؤتمر",
  "table.dates": "التواريخ",
  "table.status": "الحالة",
  "table.registrations": "التسجيلات",
  "table.collected": "المحصّل",
  "table.visitors": "الزوار",
  "table.lastSynchronized": "آخر مزامنة",
  "table.connection": "الاتصال",
  "table.action": "الإجراء",
  "overview.loading": "جارٍ تحميل ملخص المؤتمرات…",
  "overview.metrics.ariaLabel": "مؤشرات محفظة المؤتمرات",
  "overview.error.title": "النظرة العامة غير متاحة",
  "overview.error.description":
    "تعذر تحميل بيانات المؤتمرات الآن. حاول مجددًا؛ بياناتك المحفوظة لم تتغير.",
  "overview.empty.title": "لا توجد مؤتمرات متاحة",
  "overview.empty.adminDescription":
    "اربط مشروع مؤتمر على Firebase لبدء المحفظة.",
  "overview.empty.viewerDescription":
    "لم يُمنح حسابك صلاحية الوصول إلى أي مؤتمر حتى الآن.",
  "overview.sourceDelayWarning":
    "بعض المصادر متأخرة، وتظل آخر القيم الناجحة ظاهرة.",
  "overview.performance.title": "أداء المؤتمرات",
  "overview.performance.description": "أحدث لقطة مجمّعة لكل مؤتمر متصل.",
  "overview.performance.caption":
    "بيانات التسجيل والإيرادات والزوار والمزامنة المجمّعة للمؤتمرات",
  "overview.performance.viewConference": "عرض المؤتمر",
  "overview.visitors.title": "زوار المحفظة",
  "overview.visitors.unavailable": "غير متاح لهذه المؤتمرات",
  "overview.visitors.trendAriaLabel": "اتجاه زوار محفظة المؤتمرات",
  "overview.visitors.historyAccumulating": "لا يزال سجل الاتجاه قيد التجميع.",
  "overview.analytics.estimateNotice": "قيم التحليلات تقديرية وقد تتأخر.",
  "overview.analytics.partialCoverage": "تغطية جزئية للتحليلات",
  "overview.analytics.last30Days": "آخر 30 يومًا من اللقطات المتاحة",
  "directory.loading": "جارٍ تحميل المؤتمرات…",
  "directory.error.title": "قائمة المؤتمرات غير متاحة",
  "directory.error.description": "تعذر تحميل قائمة المؤتمرات المجمّعة.",
  "directory.controls.ariaLabel": "عناصر التحكم في قائمة المؤتمرات",
  "directory.filters.ariaLabel": "تصفية المؤتمرات",
  "directory.filter.all": "الكل",
  "directory.search.label": "البحث في المؤتمرات",
  "directory.search.placeholder": "ابحث في المؤتمرات…",
  "directory.sort.label": "الترتيب حسب",
  "directory.sort.eventDate": "تاريخ الفعالية",
  "directory.sort.name": "الاسم",
  "directory.sort.lastSynchronized": "آخر مزامنة",
  "directory.empty.title": "لا توجد مؤتمرات مطابقة",
  "directory.empty.description": "غيّر مرشح الحالة أو عبارة البحث.",
  "directory.table.caption":
    "تواريخ المؤتمرات المتصلة ومؤشراتها ومزامنتها وحالة اتصالها",
  "directory.action.open": "فتح",
  "directory.action.disable": "تعطيل",
  "directory.disable.title": "تعطيل اتصال المؤتمر؟",
  "directory.disable.descriptionSuffix":
    "سيظل ظاهرًا مع آخر لقطة مجمّعة ناجحة. ولن تؤدي المزامنة الجديدة إلى إعادة تفعيله.",
  "directory.disable.error": "تعذر تعطيل المؤتمر.",
  "directory.disable.busy": "جارٍ التعطيل…",
  "directory.disable.confirm": "تعطيل المؤتمر",
  "brand.taibahUniversity": "جامعة طيبة",
  "brand.taibahValley": "وادي طيبة",
  "brand.deliveredBy": "تطوير وتنفيذ",
  "brand.partnerSignature": "تصميم وتطوير وتنفيذ شركة وادي طيبة",
  "brand.partnerCredit": "تصميم وتطوير وتنفيذ",
  "brand.universityIdentityAria": "هوية جامعة طيبة",
  "auth.dashboardLabel": "لوحة المتابعة التنفيذية لمؤتمرات جامعة طيبة",
  "auth.subtitle": "سجّل الدخول لمتابعة أداء المؤتمرات.",
  "auth.email": "البريد الإلكتروني",
  "auth.password": "كلمة المرور",
  "auth.showPassword": "إظهار كلمة المرور",
  "auth.hidePassword": "إخفاء كلمة المرور",
  "auth.signingIn": "جارٍ تسجيل الدخول…",
  "auth.signIn": "تسجيل الدخول",
  "auth.or": "أو",
  "auth.embeddedTitle": "يتطلب تسجيل Google متصفح Chrome أو Safari.",
  "auth.embeddedBody":
    "هذا المتصفح المضمّن غير مدعوم من Google. افتح رابط اللوحة نفسه في متصفح مستقل للمتابعة.",
  "auth.openDashboardLink": "فتح رابط اللوحة",
  "auth.continueGoogle": "المتابعة باستخدام Google",
  "auth.oauthHelp":
    "إذا كانت اللوحة مفتوحة داخل تطبيق آخر، فانسخ رابطها وافتحه في Chrome أو Safari مستقل قبل استخدام تسجيل Google.",
  "auth.authorizedOnly": "للمستخدمين المصرح لهم فقط.",
  "auth.signOut": "تسجيل الخروج",
  "auth.mfaConfiguredNotice":
    "اكتمل إعداد المصادقة متعددة العوامل. سجّل الدخول مجددًا للتحقق من الرمز.",
  "auth.error.invalidCredentials":
    "البريد الإلكتروني أو كلمة المرور غير صحيحة.",
  "auth.error.accountDisabled": "تم تعطيل هذا الحساب.",
  "auth.error.accountNotProvisioned":
    "لم تتم تهيئة حساب Google هذا للوصول إلى اللوحة.",
  "auth.error.accessDenied": "هذا الحساب غير مصرح له بالوصول إلى اللوحة.",
  "auth.error.googleUnavailable":
    "تعذر فتح تسجيل Google. اسمح بالنوافذ المنبثقة أو استخدم Chrome أو Safari مستقلًا ثم أعد المحاولة.",
  "auth.error.invalidMfa": "رمز التحقق غير صحيح أو انتهت صلاحيته.",
  "auth.error.network": "تعذر الوصول إلى الخدمة. أعد المحاولة.",
  "auth.error.popupClosed": "تم إلغاء تسجيل الدخول باستخدام Google.",
  "auth.error.sessionExpired": "انتهت الجلسة الآمنة. سجّل الدخول مرة أخرى.",
  "auth.error.unexpected": "تعذر إكمال تسجيل الدخول.",
  "mfa.enrollTitle": "إعداد المصادقة متعددة العوامل",
  "mfa.challengeTitle": "تحقق من هويتك",
  "mfa.enrollInstruction":
    "أضف هذا الحساب إلى تطبيق المصادقة، ثم أدخل الرمز المكوّن من ستة أرقام.",
  "mfa.challengeInstruction":
    "أدخل الرمز المكوّن من ستة أرقام من تطبيق المصادقة.",
  "mfa.scanTitle": "امسح رمز QR",
  "mfa.scanHelp": "استخدم Google Authenticator أو Microsoft Authenticator.",
  "mfa.qrAlt": "رمز QR لإعداد {issuer} للحساب {account}",
  "mfa.qrUnavailable": "رمز QR غير متاح",
  "mfa.qrUnavailableBody":
    "استخدم مفتاح الإعداد أو حاول إنشاء رمز QR مرة أخرى.",
  "mfa.qrRetry": "إعادة إنشاء رمز QR",
  "mfa.qrLoading": "جارٍ إنشاء رمز QR…",
  "mfa.accountTitle": "تأكيد الحساب",
  "mfa.accountHelp": "ستظهر هذه البيانات في تطبيق المصادقة.",
  "mfa.service": "الخدمة",
  "mfa.account": "الحساب",
  "mfa.manualTitle": "يتعذر مسح رمز QR؟",
  "mfa.manualHelp": "أدخل مفتاح الإعداد هذا في تطبيق المصادقة بدلًا منه.",
  "mfa.manualKeyAria": "مفتاح الإعداد اليدوي",
  "mfa.copying": "جارٍ النسخ…",
  "mfa.copyKey": "نسخ مفتاح الإعداد",
  "mfa.keyCopied": "تم نسخ مفتاح الإعداد.",
  "mfa.copyFailed": "تعذر النسخ. حدّد المفتاح وانسخه يدويًا.",
  "mfa.verifySetupTitle": "التحقق من الإعداد",
  "mfa.verifySetupHelp":
    "أدخل الرمز الحالي المكوّن من ستة أرقام لإكمال الإعداد.",
  "mfa.codeLabel": "رمز التحقق",
  "mfa.verifying": "جارٍ التحقق…",
  "mfa.finishSetup": "إكمال الإعداد",
  "mfa.verifyCode": "التحقق من الرمز",
  "mfa.backToSignIn": "العودة إلى تسجيل الدخول",
  "nav.overview": "نظرة عامة",
  "nav.conferences": "المؤتمرات",
  "nav.revenue": "الإيرادات",
  "nav.expenses": "المصروفات",
  "nav.users": "المستخدمون",
  "nav.connect": "ربط مؤتمر",
  "nav.more": "المزيد",
  "a11y.skipMain": "الانتقال إلى المحتوى الرئيسي",
  "a11y.primaryNavigation": "التنقل الرئيسي",
  "a11y.mobileNavigation": "تنقل الأجهزة المحمولة",
  "a11y.moreNavigation": "خيارات تنقل إضافية",
  "a11y.restoringSession": "جارٍ استعادة الجلسة الآمنة",
  "role.executiveAdmin": "مسؤول النظام",
  "role.executiveViewer": "للاطلاع فقط",
  "role.executiveAdminInitials": "م ن",
  "role.executiveViewerInitials": "ق",
  "theme.useLight": "استخدام المظهر الفاتح",
  "theme.useDark": "استخدام المظهر الداكن",
  "session.authenticated": "تم تسجيل الدخول",
  "page.overviewTitle": "محفظة المؤتمرات",
  "page.overviewSubtitle": "نظرة تنفيذية عامة",
  "page.conferencesTitle": "المؤتمرات",
  "page.conferencesSubtitle": "متابعة المؤتمرات النشطة والقادمة والمنتهية.",
  "page.revenueTitle": "الإيرادات",
  "page.revenueSubtitle":
    "متابعة الرعايات والفواتير والدفعات والإيرادات الأخرى.",
  "page.expensesTitle": "المصروفات",
  "page.expensesSubtitle":
    "تسجيل المصروفات الفعلية للمؤتمرات ومراجعتها حسب النوع.",
  "page.adminAccessRequired": "تتطلب هذه الصفحة صلاحية مسؤول النظام.",
  "expense.heading": "مصروفات المؤتمر",
  "expense.description":
    "المبالغ المصروفة فعليًا على هذا المؤتمر. تبقى القيود المعكوسة ظاهرة لأغراض التدقيق.",
  "expense.conference": "المؤتمر",
  "expense.form.add": "إضافة مصروف",
  "expense.form.heading": "تسجيل مصروف",
  "expense.form.category": "نوع المصروف",
  "expense.form.title": "مسمى المصروف",
  "expense.form.titleHint": "مثال: تشغيل القاعة أو سفر المتحدثين",
  "expense.form.amount": "القيمة",
  "expense.form.date": "تاريخ المصروف",
  "expense.form.record": "تسجيل المصروف",
  "expense.form.invalid": "أدخل مسمى وقيمة وتاريخًا صحيحًا.",
  "expense.form.recorded": "تم تسجيل المصروف بنجاح.",
  "expense.form.failed": "تعذر تسجيل المصروف. حاول مرة أخرى.",
  "expense.category.operations": "التشغيل",
  "expense.category.marketing": "التسويق",
  "expense.category.hospitality": "الضيافة",
  "expense.category.technology": "التقنية",
  "expense.category.speakers": "المتحدثون",
  "expense.category.travelAccommodation": "السفر والسكن",
  "expense.category.equipment": "التجهيزات",
  "expense.category.other": "أخرى",
  "expense.summary.aria": "ملخص المصروفات الفعلية",
  "expense.summary.total": "إجمالي المصروف",
  "expense.summary.count": "القيود الفعالة",
  "expense.loading": "جارٍ تحميل المصروفات…",
  "expense.loadFailed": "تعذر تحميل المصروفات.",
  "expense.unavailableShort": "غير متاح",
  "expense.unavailable.heading": "المصروفات غير متاحة",
  "expense.unavailable.description":
    "تعذر تحميل قائمة المؤتمرات. حاول مرة أخرى لاحقًا.",
  "expense.noConferences.heading": "لا توجد مؤتمرات متاحة",
  "expense.noConferences.description": "اربط مؤتمرًا قبل تسجيل المصروفات.",
  "expense.empty.heading": "لا توجد مصروفات مسجلة",
  "expense.empty.description": "ستظهر هنا مصروفات المؤتمر بعد تسجيلها.",
  "expense.column.title": "المصروف",
  "expense.column.category": "النوع",
  "expense.column.date": "التاريخ",
  "expense.column.amount": "القيمة",
  "expense.column.status": "الحالة",
  "expense.column.action": "الإجراء",
  "expense.status.active": "فعال",
  "expense.status.reversed": "معكوس",
  "expense.edit.action": "تعديل",
  "expense.edit.heading": "تعديل المصروف",
  "expense.edit.description":
    "عدّل بيانات المصروف الفعّال. يُسجل التعديل لأغراض التدقيق.",
  "expense.edit.close": "إغلاق محرر المصروف",
  "expense.edit.save": "حفظ التعديل",
  "expense.edit.invalid": "أدخل مسمى وقيمة وتاريخًا صحيحًا.",
  "expense.edit.recorded": "تم تعديل المصروف بنجاح.",
  "expense.edit.failed": "تعذر تعديل المصروف. حدّث الصفحة وحاول مرة أخرى.",
  "expense.reverse.action": "عكس القيد",
  "expense.reverse.confirm":
    "هل تريد عكس هذا المصروف؟ سيبقى القيد الأصلي محفوظًا في سجل التدقيق.",
  "expense.reverse.busy": "جارٍ العكس…",
  "expense.reverse.recorded": "تم العكس",
  "expense.reverse.failed": "تعذر عكس المصروف. حاول مرة أخرى.",
  "page.connectTitle": "ربط مؤتمر",
  "page.connectSubtitle":
    "جلب بيانات المؤتمر من مشروع Firebase الخاص به ومراجعة الاتصال.",
  "page.usersTitle": "المستخدمون",
  "page.usersSubtitle": "صلاحيات العرض وأحداث التدقيق الإداري.",
  "page.plannedTitle": "مساحة عمل مخططة",
  "page.plannedBody": "ستتاح هذه المساحة في خطوة التنفيذ المعتمدة الخاصة بها.",
  "page.returnOverview": "العودة إلى النظرة العامة",
} as const satisfies TranslationCatalog;

export const TRANSLATIONS = Object.freeze({
  "ar-SA": ARABIC_MESSAGES,
  en: ENGLISH_MESSAGES,
}) satisfies Readonly<Record<AppLocale, TranslationCatalog>>;

const PLACEHOLDER_PATTERN = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

function messageTemplate(
  locale: AppLocale,
  definition: MessageDefinition,
  parameters: MessageParameters,
): string {
  if (typeof definition === "string") return definition;

  const count = parameters.count;
  if (typeof count !== "number" || !Number.isFinite(count)) {
    return definition.other;
  }

  const category = new Intl.PluralRules(locale).select(count);
  return definition[category] ?? definition.other;
}

export function translate(
  locale: AppLocale,
  key: MessageKey,
  parameters: MessageParameters = {},
): string {
  const template = messageTemplate(
    locale,
    TRANSLATIONS[locale][key],
    parameters,
  );
  const localizedCount =
    typeof parameters.count === "number" && Number.isFinite(parameters.count)
      ? new Intl.NumberFormat(locale, { numberingSystem: "latn" }).format(
          parameters.count,
        )
      : undefined;

  return template.replace(PLACEHOLDER_PATTERN, (placeholder, name: string) => {
    if (!Object.hasOwn(parameters, name)) return placeholder;
    if (name === "count" && localizedCount !== undefined) return localizedCount;
    const value = parameters[name];
    return value === undefined ? placeholder : String(value);
  });
}
